import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Card, Col, Input, Popconfirm, Row, Segmented, Select, Spin } from 'antd';
import { DeleteOutlined, PlusOutlined, SaveOutlined, SearchOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { selectUser } from '@/store/features/userinfo/userinfoSlice';
import { RootState } from '@/store';
import { selectApexAccounts } from '@/store/features/apex/apex-selectors';
import { fetchApexAccounts } from '@/store/features/apex/apex-thunks';
import { useFirmDataEnabled } from '@/hooks/useFirmDataEnabled';
import { RiskProfile } from './components';
import {
  buildProfilePayloadPatch,
  defaultProfilePayloadFormValues,
  formatCurrency,
  getEditableProfilePayloadFields,
  validateProfilePayloadForm,
  type ProfilePayloadFieldValidationMessage,
  type ProfilePayloadFormValues,
  type ProfilePayloadInputField,
  type ProfilePayloadPatch,
} from '@wavvest/shared-utils';
import {
  fetchInvestorProfileV1,
  type InvestorProfilePayload,
  updateInvestorProfileV1,
  getRiskScores,
  updateRiskScore,
  type RiskScoreRecord,
} from '@/api/custom';
import { accountTypeToName } from '@/util/planning-util';
import './index.scss';
import { notifyRiskScoresChanged } from '@/hooks/risk-score-events';

type ProfileFormState = {
  fullName: string;
  dateOfBirth: string;
};

const emptyFormState: ProfileFormState = {
  fullName: '',
  dateOfBirth: '',
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const getInvestorSection = (
  payload: InvestorProfilePayload | null,
): Record<string, unknown> | null => {
  const sections = asRecord(payload?.sections);
  return asRecord(sections?.investor);
};

const getSpouseSection = (
  payload: InvestorProfilePayload | null,
): Record<string, unknown> | null => {
  const sections = asRecord(payload?.sections);
  return asRecord(sections?.spouse);
};

const getTaxSection = (payload: InvestorProfilePayload | null): Record<string, unknown> | null => {
  const sections = asRecord(payload?.sections);
  return asRecord(sections?.tax);
};

const getStringField = (
  record: Record<string, unknown> | null,
  fieldName: string,
): string | null => {
  const value = record?.[fieldName];
  if (typeof value === 'string' && value.length > 0) return value;
  const nestedValue = asRecord(value);
  if (typeof nestedValue?.display_name === 'string' && nestedValue.display_name.length > 0) {
    return nestedValue.display_name;
  }
  return null;
};

const getNamePart = (
  section: Record<string, unknown> | null,
  fieldName: string,
  partName: string,
): string | null => {
  const name = asRecord(section?.[fieldName]);
  const value = name?.[partName];
  return typeof value === 'string' && value.length > 0 ? value : null;
};

const getAddressDisplay = (section: Record<string, unknown> | null): string | null => {
  const address = asRecord(section?.primary_residence_address);
  if (!address) return null;

  if (typeof address.display_name === 'string' && address.display_name.length > 0) {
    return address.display_name;
  }

  const parts = [
    address.line1,
    address.line2,
    address.city,
    address.state,
    address.postal_code,
    address.country,
  ].filter((part): part is string => typeof part === 'string' && part.length > 0);

  return parts.length > 0 ? parts.join(', ') : null;
};

const PROFILE_CHOICE_LABELS: Record<string, string> = {
  MARRIED_FILING_JOINTLY: 'Married filing jointly',
  MARRIED_FILING_SEPARATELY: 'Married filing separately',
  HEAD_OF_HOUSEHOLD: 'Head of household',
  QUALIFYING_WIDOW: 'Qualifying widow',
};

const formatProfileChoice = (value: string | null): string => {
  if (!value) return '—';
  return (
    PROFILE_CHOICE_LABELS[value] ??
    value
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase())
  );
};

const profileInputType = (field: ProfilePayloadInputField): string => {
  if (field.inputKind === 'tel') return 'tel';
  if (field.inputKind === 'number') return 'text';
  return field.inputKind;
};

const profileInputMode = (field: ProfilePayloadInputField): 'decimal' | undefined =>
  field.inputKind === 'number' ? 'decimal' : undefined;

const formattedMoneyFieldValue = (value: string): string => {
  if (!value.trim()) return '';
  const numericValue = Number(value.replace(/[$,]/g, ''));
  return Number.isFinite(numericValue) ? formatCurrency(numericValue) : value;
};

const unformattedMoneyFieldValue = (value: string): string => value.replace(/[$,]/g, '');

const isProfileTextAreaField = (field: ProfilePayloadInputField): boolean =>
  field.path.endsWith('.notes');

const profileFieldColumnProps = ({
  hasAmountTimingControl,
  isTextArea,
}: {
  hasAmountTimingControl: boolean;
  isTextArea: boolean;
}) => {
  if (isTextArea) return { xs: 24, sm: 24, md: 24, lg: 24, xxl: 24 };
  if (hasAmountTimingControl) return { xs: 24, sm: 12, md: 12, lg: 8, xxl: 6 };
  return { xs: 12, sm: 12, md: 12, lg: 8, xxl: 6 };
};

const profileFieldDomId = (path: string): string =>
  `wealth-profile-field-${path.replace(/[^a-zA-Z0-9_-]+/g, '-')}`;

const profileFieldStatus = (
  messages: ProfilePayloadFieldValidationMessage[],
): 'error' | 'warning' | undefined => {
  if (messages.some((message) => message.severity === 'error')) return 'error';
  if (messages.some((message) => message.severity === 'warning')) return 'warning';
  return undefined;
};

const profileSelectOptions = (field: ProfilePayloadInputField) => {
  if (field.options?.length) {
    return [
      { value: '', label: field.required ? 'Select an option' : 'Not set' },
      ...field.options,
    ];
  }

  if (field.inputKind === 'boolean') {
    return [
      { value: '', label: 'Not set' },
      { value: 'true', label: 'True' },
      { value: 'false', label: 'False' },
    ];
  }

  return null;
};

const sectionKeyForField = (field: ProfilePayloadInputField): string => {
  const segments = field.path.split('.');
  return segments[0] === 'sections' ? (segments[1] ?? 'other') : segments[0];
};

const SPOUSE_EMPLOYMENT_FIELD_PREFIX = 'sections.spouse.employment.';
const PRIMARY_RESIDENCE_ADDRESS_FIELD_PREFIX = 'sections.investor.primary_residence_address.';
const PRIMARY_RESIDENCE_ADDRESS_FIELD_PATH = 'sections.investor.primary_residence_address';
const MAILING_ADDRESS_FIELD_PREFIX = 'sections.investor.mailing_address.';
const MAILING_ADDRESS_FIELD_PATH = 'sections.investor.mailing_address';
const PRIMARY_RESIDENCE_STATE_FIELD_PATH = 'sections.investor.primary_residence_state';
const INVESTOR_MARITAL_STATUS_FIELD_PATH = 'sections.investor.marital_status';
const SPOUSE_SECTION_FIELD_PATH = 'sections.spouse';
const INVESTOR_EMPLOYMENT_FIELD_PATH = 'sections.investor.employment';
const SPOUSE_EMPLOYMENT_FIELD_PATH = 'sections.spouse.employment';
const SPOUSE_ANNUAL_INCOME_FIELD_PATH = 'sections.spouse.annual_gross_income';
const CITIZENSHIP_STATUS_FIELD_PATH = 'sections.investor.citizenship_status';
const LEGACY_CITIZENSHIP_STATUS_FIELD_PATH = 'sections.investor.citizenship';
const PRIMARY_CITIZENSHIP_COUNTRY_FIELD_PATH = 'sections.investor.citizenship_country_primary';
const SECONDARY_CITIZENSHIP_COUNTRY_FIELD_PATH = 'sections.investor.citizenship_country_secondary';
const DEPENDENT_REPEATABLE_ROOT_PATH = 'sections.dependents';
const INCOME_REPEATABLE_ROOT_PATH = 'sections.income_sources';
const EXPENSE_REPEATABLE_ROOT_PATH = 'sections.expenses';
const INCOME_SOURCE_PREFIX = 'Income source ';
const isIncomeSourceNameFieldPath = (fieldPath: string): boolean =>
  /^sections\.income_sources\.\d+\.name$/.test(fieldPath);
const isIncomeSourceAmountFieldPath = (fieldPath: string): boolean =>
  /^sections\.income_sources\.\d+\.(gross_annual_amount|amount)$/.test(fieldPath);
const isIncomeSourceTypeFieldPath = (fieldPath: string): boolean =>
  /^sections\.income_sources\.\d+\.source_type$/.test(fieldPath);
const isIncomeSourceFrequencyFieldPath = (fieldPath: string): boolean =>
  /^sections\.income_sources\.\d+\.frequency$/.test(fieldPath);
const isIncomeSourceSharedWithSpouseFieldPath = (fieldPath: string): boolean =>
  /^sections\.income_sources\.\d+\.(shared_with_spouse|joint_with_spouse)$/.test(fieldPath);
const isBusinessNameFieldPath = (fieldPath: string): boolean =>
  /^sections\.(?:business\.businesses|businesses)\.\d+\.name$/.test(fieldPath);
const isBusinessOwnsBusinessFieldPath = (fieldPath: string): boolean =>
  fieldPath === 'sections.business.owns_business';
const isBusinessEstimatedValueFieldPath = (fieldPath: string): boolean =>
  /^sections\.(?:business\.businesses|businesses)\.\d+\.estimated_value$/.test(fieldPath);
const isBusinessJointWithSpouseFieldPath = (fieldPath: string): boolean =>
  /^sections\.(?:business\.businesses|businesses)\.\d+\.joint_with_spouse$/.test(fieldPath);
const isProfileSharedWithSpouseFieldPath = (fieldPath: string): boolean =>
  isIncomeSourceSharedWithSpouseFieldPath(fieldPath) ||
  isBusinessJointWithSpouseFieldPath(fieldPath);
const expenseNumberLabelForFieldPath = (fieldPath: string): string | null => {
  const match = fieldPath.match(/^sections\.expenses\.(\d+)\./);
  return match ? `Expense ${Number(match[1]) + 1}` : null;
};
const isExpenseNameFieldPath = (fieldPath: string): boolean =>
  /^sections\.expenses\.\d+\.name$/.test(fieldPath);
const isExpenseAmountFieldPath = (fieldPath: string): boolean =>
  /^sections\.expenses\.\d+\.amount$/.test(fieldPath);
const isExpenseFrequencyFieldPath = (fieldPath: string): boolean =>
  /^sections\.expenses\.\d+\.frequency$/.test(fieldPath);
const compactExpenseFieldClassName = (fieldPath: string): string | undefined => {
  if (isExpenseNameFieldPath(fieldPath)) {
    return 'profile-compact-expense-field is-expense-name';
  }
  if (isExpenseAmountFieldPath(fieldPath)) {
    return 'profile-compact-expense-field is-expense-amount';
  }
  if (isExpenseFrequencyFieldPath(fieldPath)) {
    return 'profile-compact-expense-field is-expense-frequency';
  }
  return undefined;
};
const compactIncomeFieldClassName = (fieldPath: string): string | undefined => {
  if (isIncomeSourceNameFieldPath(fieldPath)) {
    return 'profile-compact-income-field is-income-name';
  }
  if (isIncomeSourceTypeFieldPath(fieldPath)) {
    return 'profile-compact-income-field is-income-source-type';
  }
  if (isIncomeSourceAmountFieldPath(fieldPath)) {
    return 'profile-compact-income-field is-income-amount';
  }
  if (isIncomeSourceFrequencyFieldPath(fieldPath)) {
    return 'profile-compact-income-field is-income-frequency';
  }
  if (isIncomeSourceSharedWithSpouseFieldPath(fieldPath)) {
    return 'profile-compact-income-field is-income-shared-with-spouse';
  }
  return undefined;
};
const compactBusinessFieldClassName = (fieldPath: string): string | undefined => {
  if (isBusinessNameFieldPath(fieldPath)) {
    return 'profile-compact-business-field is-business-name';
  }
  if (isBusinessOwnsBusinessFieldPath(fieldPath)) {
    return 'profile-compact-business-field is-business-owns';
  }
  if (isBusinessEstimatedValueFieldPath(fieldPath)) {
    return 'profile-compact-business-field is-business-estimated-value';
  }
  if (isBusinessJointWithSpouseFieldPath(fieldPath)) {
    return 'profile-compact-business-field is-business-shared-with-spouse';
  }
  return undefined;
};
type AmountTimingValue = 'ANNUAL' | 'MONTHLY' | 'ONE_TIME';
const BASIC_AMOUNT_TIMING_OPTIONS: { label: string; value: AmountTimingValue }[] = [
  { label: 'Yearly', value: 'ANNUAL' },
  { label: 'One-time', value: 'ONE_TIME' },
];
const EXPENSE_AMOUNT_TIMING_OPTIONS: { label: string; value: AmountTimingValue }[] = [
  { label: 'Yearly', value: 'ANNUAL' },
  { label: 'Monthly', value: 'MONTHLY' },
  { label: 'One-time', value: 'ONE_TIME' },
];
const SHARED_WITH_SPOUSE_OPTIONS = [
  { label: 'No', value: 'false' },
  { label: 'Yes', value: 'true' },
];
const UNSET_SHARED_WITH_SPOUSE_OPTIONS = [
  ...SHARED_WITH_SPOUSE_OPTIONS,
  { label: 'Not set', value: '' },
];
const sharedWithSpouseSegmentedValue = (value: unknown): '' | 'true' | 'false' => {
  if (value === true || value === 'true') return 'true';
  if (value === false || value === 'false') return 'false';
  return '';
};
const sharedWithSpouseOptionsForValue = (value: '' | 'true' | 'false') =>
  value ? SHARED_WITH_SPOUSE_OPTIONS : UNSET_SHARED_WITH_SPOUSE_OPTIONS;
const normalizeIncomeTimingValue = (value: unknown): AmountTimingValue =>
  value === 'ONE_TIME' ? 'ONE_TIME' : 'ANNUAL';
const normalizeExpenseTimingValue = (value: unknown): AmountTimingValue =>
  value === 'ONE_TIME' ? 'ONE_TIME' : value === 'MONTHLY' ? 'MONTHLY' : 'ANNUAL';
const normalizeProfilePayloadFormValues = (
  values: ProfilePayloadFormValues,
): ProfilePayloadFormValues =>
  Object.fromEntries(
    Object.entries(values).map(([path, value]) => {
      if (isIncomeSourceFrequencyFieldPath(path)) {
        return [path, normalizeIncomeTimingValue(value)];
      }
      if (isExpenseFrequencyFieldPath(path)) {
        return [path, normalizeExpenseTimingValue(value)];
      }
      if (isProfileSharedWithSpouseFieldPath(path)) {
        return [path, sharedWithSpouseSegmentedValue(value)];
      }
      return [path, value];
    }),
  );
const amountTimingOptionsForFieldPath = (fieldPath: string) =>
  fieldPath.startsWith(`${EXPENSE_REPEATABLE_ROOT_PATH}.`)
    ? EXPENSE_AMOUNT_TIMING_OPTIONS
    : BASIC_AMOUNT_TIMING_OPTIONS;
const frequencyPathForTimedAmountPath = (fieldPath: string): string | null => {
  const match = fieldPath.match(
    /^(sections\.(?:income_sources|expenses)\.\d+)\.(?:gross_annual_amount|amount)$/,
  );
  return match ? `${match[1]}.frequency` : null;
};
const amountTimingValue = (fieldPath: string, value: unknown): AmountTimingValue | null => {
  if (isIncomeSourceFrequencyFieldPath(fieldPath)) return normalizeIncomeTimingValue(value);
  if (isExpenseFrequencyFieldPath(fieldPath)) return normalizeExpenseTimingValue(value);
  return null;
};
const isHiddenProfileField = (fieldPath: string): boolean =>
  isIncomeSourceFrequencyFieldPath(fieldPath) || isExpenseFrequencyFieldPath(fieldPath);
const defaultClientProfilePayloadFormValues = (
  payload: InvestorProfilePayload,
): ProfilePayloadFormValues =>
  normalizeProfilePayloadFormValues(defaultProfilePayloadFormValues(payload));
const PROPERTY_REPEATABLE_ROOT_PATH = 'sections.properties';
const BUSINESS_REPEATABLE_ROOT_PATH = 'sections.business.businesses';
const LEGACY_BUSINESS_REPEATABLE_ROOT_PATH = 'sections.businesses';
const GOAL_REPEATABLE_ROOT_PATH = 'sections.goals';
const BENEFICIARY_CANDIDATE_REPEATABLE_ROOT_PATH = 'sections.beneficiary_candidates';
const BENEFICIARY_CANDIDATE_SOURCE_STATUS_FIELD_PATH =
  'sections.beneficiary_candidates[].source_status';
const RETIREMENT_SECTION_DESCRIPTION = "Enter retirement spending in today's dollars.";
const BENEFICIARY_CANDIDATE_SECTION_DESCRIPTION =
  'Use this planning section to capture people who may be considered as beneficiaries. These entries are not confirmed legal or institutional beneficiary designations.';
const GOAL_FIELD_ORDER = [
  'sections.goals[].category',
  'sections.goals[].target_amount',
  'sections.goals[].inflation_rate',
  'sections.goals[].target_date',
  'sections.goals[].priority',
  'sections.goals',
];
const RETIREMENT_RISK_FIELD_ORDER = [
  'sections.retirement.desired_annual_spending',
  'sections.retirement.target_age',
  'sections.retirement.spouse_target_age',
  'sections.retirement',
  'sections.risk',
];
const DEPENDENT_FIELD_ORDER = [
  'sections.dependents[].name.given_name',
  'sections.dependents[].name.middle_names',
  'sections.dependents[].name.family_name',
  'sections.dependents[].name.name_suffix',
  'sections.dependents[].date_of_birth',
  'sections.dependents[].relationship',
  'sections.dependents',
];
const INCOME_SOURCE_FIELD_ORDER = [
  'sections.income_sources[].name',
  'sections.income_sources[].source_type',
  'sections.income_sources[].gross_annual_amount',
  'sections.income_sources[].amount',
  'sections.income_sources[].frequency',
  'sections.income_sources[].shared_with_spouse',
  'sections.income_sources[].joint_with_spouse',
  'sections.income_sources[].source_status',
  'sections.income_sources',
];
const EXPENSE_FIELD_ORDER = [
  'sections.expenses[].name',
  'sections.expenses[].amount',
  'sections.expenses[].frequency',
  'sections.expenses.total_fixed_monthly',
  'sections.expenses.total_variable_monthly',
  'sections.expenses.annual_one_time',
  'sections.expenses',
];
const BUSINESS_FIELD_ORDER = [
  'sections.business.businesses[].name',
  'sections.businesses[].name',
  'sections.business.owns_business',
  'sections.business.businesses[].estimated_value',
  'sections.businesses[].estimated_value',
  'sections.business.businesses[].joint_with_spouse',
  'sections.businesses[].joint_with_spouse',
  'sections.business.businesses',
  'sections.businesses',
  'sections.business',
];
const INVESTOR_CONTACT_RESIDENCE_FIELD_PATHS = [
  'sections.investor.email',
  'sections.investor.phone_number',
  'sections.investor.primary_residence_address',
  'sections.investor.primary_residence_state',
  'sections.investor.mailing_address',
  'sections.investor.address',
];
const addressFieldOrder = (addressPath: string): string[] => [
  `${addressPath}.line1`,
  `${addressPath}.line_1`,
  `${addressPath}.address_line1`,
  `${addressPath}.address_line_1`,
  `${addressPath}.line2`,
  `${addressPath}.line_2`,
  `${addressPath}.address_line2`,
  `${addressPath}.address_line_2`,
  `${addressPath}.city`,
  `${addressPath}.state`,
  `${addressPath}.region`,
  `${addressPath}.postal_code`,
  `${addressPath}.country`,
];
const PRIMARY_RESIDENCE_ADDRESS_FIELD_ORDER = addressFieldOrder(
  PRIMARY_RESIDENCE_ADDRESS_FIELD_PATH,
);
const MAILING_ADDRESS_FIELD_ORDER = addressFieldOrder(MAILING_ADDRESS_FIELD_PATH);

const sectionRelativeFieldLabel = (label: string): string => {
  const idx = label.indexOf(' / ');
  if (idx === -1) return label;
  const first = label.slice(0, idx);
  return /\d$/.test(first) ? label : label.slice(idx + 3);
};

type ProfileFieldSection = {
  id: string;
  title: string;
  description?: string;
  fields: ProfilePayloadInputField[];
};

type ProfileFieldSubsection = {
  id: string;
  title: string;
  description?: string;
  repeatableRootPath?: string;
  repeatableIndex?: number;
};

type ProfileFieldGroup = {
  id: string;
  title?: string;
  description?: string;
  repeatableRootPath?: string;
  repeatableIndex?: number;
  fields: ProfilePayloadInputField[];
};

const BUSINESS_ROW_GROUP_ID_PATTERN = /^business-\d+$/;

type ProfileSectionDefinition = {
  id: string;
  label: string;
  description?: string;
  matches: (field: ProfilePayloadInputField) => boolean;
  fieldOrder: string[];
};

const capitalizeFieldLabel = (label: string): string =>
  label.charAt(0).toUpperCase() + label.slice(1);

const withoutLeadingLabelSegment = (label: string, leadingSegment: string): string => {
  const slashPrefix = `${leadingSegment} / `;
  if (label.startsWith(slashPrefix)) return label.slice(slashPrefix.length);

  const wordPrefix = `${leadingSegment} `;
  if (label.startsWith(wordPrefix)) return capitalizeFieldLabel(label.slice(wordPrefix.length));

  return label;
};

const displayFieldLabel = (
  field: ProfilePayloadInputField,
  subsection?: ProfileFieldSubsection,
): string => {
  const label = sectionRelativeFieldLabel(field.label);
  if (field.path.startsWith(PRIMARY_RESIDENCE_ADDRESS_FIELD_PREFIX)) {
    return label.replace(/^Primary residence address \/ /, '');
  }
  if (field.path.startsWith(MAILING_ADDRESS_FIELD_PREFIX)) {
    return label.replace(/^Mailing address \/ /, '');
  }
  if (field.path === PRIMARY_RESIDENCE_STATE_FIELD_PATH) {
    return 'State';
  }

  const employmentPrefix = 'Employment / ';
  const fieldLabel = label.startsWith(employmentPrefix)
    ? label.slice(employmentPrefix.length)
    : label;
  let nextLabel = field.path.startsWith(SPOUSE_EMPLOYMENT_FIELD_PREFIX)
    ? `Spouse employment / ${fieldLabel}`
    : label;

  if (subsection) {
    nextLabel = withoutLeadingLabelSegment(nextLabel, subsection.title);
  }

  if (field.path.startsWith(`${INCOME_REPEATABLE_ROOT_PATH}.`)) {
    nextLabel = nextLabel.replace(new RegExp(`^${escapeRegExp(INCOME_SOURCE_PREFIX)}\\d+ / `), '');
    if (isIncomeSourceAmountFieldPath(field.path)) {
      nextLabel = 'Amount';
    }
    if (field.path.endsWith('.joint_with_spouse')) {
      nextLabel = 'Shared';
    }
  }
  if (isBusinessJointWithSpouseFieldPath(field.path)) {
    nextLabel = 'Shared';
  }
  if (isBusinessOwnsBusinessFieldPath(field.path)) {
    nextLabel = 'Owner';
  }
  if (fieldPathMatches(field.path, SPOUSE_ANNUAL_INCOME_FIELD_PATH)) {
    nextLabel = 'Gross income amount';
  }
  const expenseNumberLabel = expenseNumberLabelForFieldPath(field.path);
  if (expenseNumberLabel && isExpenseNameFieldPath(field.path)) {
    nextLabel = `${expenseNumberLabel} Name`;
  }
  if (isExpenseAmountFieldPath(field.path)) {
    nextLabel = 'Amount';
  }
  if (field.path.startsWith(`${BENEFICIARY_CANDIDATE_REPEATABLE_ROOT_PATH}.`)) {
    nextLabel = nextLabel.replace(/^Beneficiary candidate \d+ \/ /, '');
  }

  if (field.path.includes('.address.')) {
    nextLabel = withoutLeadingLabelSegment(nextLabel, 'Address');
  }
  if (field.path.includes('.name.')) {
    nextLabel = withoutLeadingLabelSegment(nextLabel, 'Name');
  }
  if (field.path.startsWith(`${INVESTOR_EMPLOYMENT_FIELD_PATH}.`)) {
    nextLabel = withoutLeadingLabelSegment(nextLabel, 'Employment');
  }

  return nextLabel;
};

const fieldAriaLabel = (
  field: ProfilePayloadInputField,
  subsection?: ProfileFieldSubsection,
): string | undefined => {
  const label = displayFieldLabel(field, subsection);
  const expenseNumberLabel = expenseNumberLabelForFieldPath(field.path);
  if (expenseNumberLabel && isExpenseNameFieldPath(field.path)) return `${expenseNumberLabel} Name`;
  if (expenseNumberLabel && isExpenseAmountFieldPath(field.path)) {
    return `${expenseNumberLabel} Amount`;
  }
  return subsection ? `${subsection.title} / ${label}` : label;
};

const fieldPathMatches = (fieldPath: string, pathPrefix: string): boolean =>
  fieldPath === pathPrefix || fieldPath.startsWith(`${pathPrefix}.`);

const fieldPathMatchesAny = (fieldPath: string, pathPrefixes: string[]): boolean =>
  pathPrefixes.some((pathPrefix) => fieldPathMatches(fieldPath, pathPrefix));

const fieldBelongsToSectionKey = (
  field: ProfilePayloadInputField,
  sectionKeys: string[],
): boolean => sectionKeys.includes(sectionKeyForField(field));

const PROFILE_PORTFOLIO_FIELD_PATHS = ['sections.accounts', 'sections.debts'];
const PROFILE_PORTFOLIO_FIELD_PATTERNS = ['sections.properties[].mortgage_balance'];
const PROPERTY_TYPE_FIELD_KEY = 'property_type';
const PROPERTY_TYPE_METADATA_PATH = `${PROPERTY_REPEATABLE_ROOT_PATH}[].${PROPERTY_TYPE_FIELD_KEY}`;
const PRIMARY_RESIDENCE_PROPERTY_TYPE = 'PRIMARY_RESIDENCE';
const REPEATABLE_READ_ONLY_FIELD_KEYS = new Set(['editable_from_profile', 'mortgage_balance']);
const REPEATABLE_IDENTIFIER_FIELD_KEYS = new Set([
  'account_id',
  'candidate_id',
  'component_id',
  'debt_id',
  'property_id',
]);
const REPEATABLE_NON_USER_DATA_FIELD_KEYS = new Set([
  ...REPEATABLE_IDENTIFIER_FIELD_KEYS,
  ...REPEATABLE_READ_ONLY_FIELD_KEYS,
  'source_status',
]);

type RepeatableRowOverridesByRootPath = Record<string, unknown[]>;

type AddableRepeatableProfileSection = {
  sectionId: string;
  rootPath: string;
  buttonLabel: string;
  fallbackRootPaths?: string[];
  requiredEditableFieldPaths?: string[];
  draftItem: (payload: InvestorProfilePayload) => Record<string, unknown>;
};

const metadataPatternForFieldPath = (fieldPath: string): string =>
  fieldPath.replace(/\.(\d+)(?=\.|$)/g, '[]');

const isProfilePortfolioFieldPath = (fieldPath: string): boolean => {
  if (fieldPathMatchesAny(fieldPath, PROFILE_PORTFOLIO_FIELD_PATHS)) return true;

  const metadataPattern = metadataPatternForFieldPath(fieldPath);
  return fieldPathMatchesAny(metadataPattern, PROFILE_PORTFOLIO_FIELD_PATTERNS);
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const isBeneficiaryCandidateSourceStatusFieldPath = (fieldPath: string): boolean =>
  fieldPath === BENEFICIARY_CANDIDATE_SOURCE_STATUS_FIELD_PATH ||
  (fieldPath.startsWith(`${BENEFICIARY_CANDIDATE_REPEATABLE_ROOT_PATH}.`) &&
    fieldPath.endsWith('.source_status'));

const hiddenProfilePayloadFieldMetadata = (metadata: unknown): Record<string, unknown> => ({
  ...(isRecord(metadata) ? metadata : {}),
  editable: false,
});

const cloneProfileValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(cloneProfileValue);
  if (isRecord(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, childValue]) => [key, cloneProfileValue(childValue)]),
    );
  }
  return value;
};

const valueAtProfilePath = (payload: unknown, path: string): unknown => {
  let current = payload;
  for (const segment of path.split('.')) {
    if (Array.isArray(current) && /^\d+$/.test(segment)) {
      current = current[Number(segment)];
      continue;
    }
    if (isRecord(current) && Object.prototype.hasOwnProperty.call(current, segment)) {
      current = current[segment];
      continue;
    }
    return undefined;
  }
  return current;
};

const setValueAtProfilePath = (target: Record<string, unknown>, path: string, value: unknown) => {
  const segments = path.split('.');
  let cursor = target;
  for (const segment of segments.slice(0, -1)) {
    const nextValue = cursor[segment];
    if (!isRecord(nextValue)) {
      cursor[segment] = {};
    }
    cursor = cursor[segment] as Record<string, unknown>;
  }
  cursor[segments[segments.length - 1]] = value;
};

const isMoneyDraftValue = (value: unknown): value is Record<string, unknown> =>
  isRecord(value) &&
  (Object.prototype.hasOwnProperty.call(value, 'amount') ||
    Object.prototype.hasOwnProperty.call(value, 'currency'));

const isPercentDraftValue = (value: unknown): value is Record<string, unknown> =>
  isRecord(value) && Object.prototype.hasOwnProperty.call(value, 'percent');

const blankRepeatableValueFromTemplate = (value: unknown): unknown => {
  if (Array.isArray(value)) return [];
  if (isMoneyDraftValue(value)) {
    return {
      amount: null,
      currency: typeof value.currency === 'string' && value.currency ? value.currency : 'USD',
    };
  }
  if (isPercentDraftValue(value)) return { percent: null };
  if (isRecord(value)) {
    return Object.fromEntries(
      Object.entries(value)
        .filter(
          ([key]) =>
            !REPEATABLE_IDENTIFIER_FIELD_KEYS.has(key) && !REPEATABLE_READ_ONLY_FIELD_KEYS.has(key),
        )
        .map(([key, childValue]) => [key, blankRepeatableValueFromTemplate(childValue)]),
    );
  }
  return null;
};

const repeatableValueHasUserData = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'boolean') return true;
  if (Array.isArray(value)) return value.some(repeatableValueHasUserData);
  if (isMoneyDraftValue(value)) return repeatableValueHasUserData(value.amount);
  if (isPercentDraftValue(value)) return repeatableValueHasUserData(value.percent);
  if (isRecord(value)) {
    return Object.entries(value).some(
      ([key, childValue]) =>
        !REPEATABLE_NON_USER_DATA_FIELD_KEYS.has(key) && repeatableValueHasUserData(childValue),
    );
  }
  return false;
};

const repeatableGroupHasUserData = (
  group: ProfileFieldGroup,
  row: unknown,
  values: ProfilePayloadFormValues,
): boolean =>
  repeatableValueHasUserData(row) ||
  group.fields.some((field) => repeatableValueHasUserData(values[field.path]));

const firstRepeatableTemplateItem = (
  payload: InvestorProfilePayload,
  rootPaths: string[],
): Record<string, unknown> | null => {
  for (const rootPath of rootPaths) {
    const value = valueAtProfilePath(payload, rootPath);
    if (Array.isArray(value)) {
      const firstItem = value.find(isRecord);
      if (firstItem) return firstItem;
    }
  }
  return null;
};

const propertyDraftItem = (payload: InvestorProfilePayload): Record<string, unknown> => {
  const template = firstRepeatableTemplateItem(payload, [PROPERTY_REPEATABLE_ROOT_PATH]);
  if (template) return blankRepeatableValueFromTemplate(template) as Record<string, unknown>;

  return {
    property_type: null,
    address: {
      line1: null,
      line2: null,
      city: null,
      region: null,
      postal_code: null,
      country: 'US',
    },
    current_value: { amount: null, currency: 'USD' },
    joint_with_spouse: null,
  };
};

const incomeDraftItem = (payload: InvestorProfilePayload): Record<string, unknown> => {
  const template = firstRepeatableTemplateItem(payload, [INCOME_REPEATABLE_ROOT_PATH]);
  if (template) {
    return {
      ...(blankRepeatableValueFromTemplate(template) as Record<string, unknown>),
      name: null,
      frequency: 'ANNUAL',
      joint_with_spouse: false,
    };
  }

  return {
    name: null,
    source_type: null,
    gross_annual_amount: { amount: null, currency: 'USD' },
    frequency: 'ANNUAL',
    joint_with_spouse: false,
  };
};

const expenseDraftItem = (): Record<string, unknown> => ({
  name: null,
  amount: { amount: null, currency: 'USD' },
  frequency: 'ANNUAL',
});

const businessDraftItem = (payload: InvestorProfilePayload): Record<string, unknown> => {
  const template = firstRepeatableTemplateItem(payload, [
    BUSINESS_REPEATABLE_ROOT_PATH,
    LEGACY_BUSINESS_REPEATABLE_ROOT_PATH,
  ]);
  if (template) {
    return {
      ...(blankRepeatableValueFromTemplate(template) as Record<string, unknown>),
      name: null,
      joint_with_spouse: false,
    };
  }

  const singletonBusiness = valueAtProfilePath(payload, 'sections.business');
  if (isRecord(singletonBusiness)) {
    const businessItemTemplate = { ...singletonBusiness };
    delete businessItemTemplate.owns_business;
    delete businessItemTemplate.businesses;
    if (Object.keys(businessItemTemplate).length > 0) {
      return {
        ...(blankRepeatableValueFromTemplate(businessItemTemplate) as Record<string, unknown>),
        joint_with_spouse: false,
      };
    }
  }

  return {
    name: null,
    estimated_value: { amount: null, currency: 'USD' },
    joint_with_spouse: false,
  };
};

const goalDraftItem = (payload: InvestorProfilePayload): Record<string, unknown> => {
  const template = firstRepeatableTemplateItem(payload, [GOAL_REPEATABLE_ROOT_PATH]);
  if (template) return blankRepeatableValueFromTemplate(template) as Record<string, unknown>;

  return {
    category: null,
    target_amount: { amount: null, currency: 'USD' },
    inflation_rate: { percent: null },
    target_date: null,
    priority: null,
  };
};

const dependentDraftItem = (payload: InvestorProfilePayload): Record<string, unknown> => {
  const template = firstRepeatableTemplateItem(payload, [DEPENDENT_REPEATABLE_ROOT_PATH]);
  if (template) return blankRepeatableValueFromTemplate(template) as Record<string, unknown>;

  return {
    name: {
      given_name: null,
      middle_names: null,
      family_name: null,
      name_suffix: null,
    },
    date_of_birth: null,
    relationship: null,
  };
};

const beneficiaryCandidateDraftItem = (): Record<string, unknown> => ({
  candidate_id: `beneficiary-candidate-${uuidv4()}`,
  name: {
    given_name: null,
    middle_names: null,
    family_name: null,
    name_suffix: null,
  },
  relationship: null,
  email: null,
  phone_number: null,
  source_status: 'INVESTOR_STATED',
  notes: null,
  last_verified_date: null,
});

const ADDABLE_REPEATABLE_PROFILE_SECTIONS: AddableRepeatableProfileSection[] = [
  {
    sectionId: 'household',
    rootPath: DEPENDENT_REPEATABLE_ROOT_PATH,
    buttonLabel: 'Add dependent',
    draftItem: dependentDraftItem,
  },
  {
    sectionId: 'income',
    rootPath: INCOME_REPEATABLE_ROOT_PATH,
    buttonLabel: 'Add income',
    draftItem: incomeDraftItem,
  },
  {
    sectionId: 'expenses',
    rootPath: EXPENSE_REPEATABLE_ROOT_PATH,
    buttonLabel: 'Add expense',
    requiredEditableFieldPaths: ['sections.expenses[].name', 'sections.expenses[].amount'],
    draftItem: expenseDraftItem,
  },
  {
    sectionId: 'beneficiary-candidates',
    rootPath: BENEFICIARY_CANDIDATE_REPEATABLE_ROOT_PATH,
    buttonLabel: 'Add candidate',
    draftItem: beneficiaryCandidateDraftItem,
  },
  {
    sectionId: 'real-estate',
    rootPath: PROPERTY_REPEATABLE_ROOT_PATH,
    buttonLabel: 'Add real estate',
    requiredEditableFieldPaths: [PROPERTY_TYPE_METADATA_PATH],
    draftItem: propertyDraftItem,
  },
  {
    sectionId: 'business',
    rootPath: BUSINESS_REPEATABLE_ROOT_PATH,
    buttonLabel: 'Add business',
    fallbackRootPaths: ['sections.business'],
    draftItem: businessDraftItem,
  },
  {
    sectionId: 'business',
    rootPath: LEGACY_BUSINESS_REPEATABLE_ROOT_PATH,
    buttonLabel: 'Add business',
    draftItem: businessDraftItem,
  },
  {
    sectionId: 'goals',
    rootPath: GOAL_REPEATABLE_ROOT_PATH,
    buttonLabel: 'Add goal',
    draftItem: goalDraftItem,
  },
];

const metadataPathSupportsRepeatableRoot = (metadataPath: string, rootPath: string): boolean =>
  metadataPath === rootPath ||
  metadataPath.startsWith(`${rootPath}.`) ||
  metadataPath.startsWith(`${rootPath}[]`);

const metadataAllowsRepeatableRoot = (
  payload: InvestorProfilePayload,
  config: AddableRepeatableProfileSection,
): boolean => {
  if (!isRecord(payload.field_metadata)) return false;

  const supportedRootPaths = [config.rootPath, ...(config.fallbackRootPaths ?? [])];
  const hasEditableRoot = Object.entries(payload.field_metadata).some(
    ([metadataPath, metadata]) => {
      if (!isRecord(metadata) || metadata.editable === false) return false;
      return supportedRootPaths.some((rootPath) =>
        metadataPathSupportsRepeatableRoot(metadataPath, rootPath),
      );
    },
  );

  if (!hasEditableRoot) return false;

  return (config.requiredEditableFieldPaths ?? []).every((requiredFieldPath) =>
    Object.entries(payload.field_metadata as Record<string, unknown>).some(
      ([metadataPath, metadata]) =>
        isRecord(metadata) &&
        metadata.editable === true &&
        metadataPatternForFieldPath(metadataPath) === requiredFieldPath,
    ),
  );
};

const addableRepeatableSectionsForPayload = (
  payload: InvestorProfilePayload,
): AddableRepeatableProfileSection[] => {
  const configsByAction = new Map<string, AddableRepeatableProfileSection>();
  for (const config of ADDABLE_REPEATABLE_PROFILE_SECTIONS) {
    const actionKey = `${config.sectionId}:${config.buttonLabel}`;
    if (configsByAction.has(actionKey)) continue;
    if (metadataAllowsRepeatableRoot(payload, config)) {
      configsByAction.set(actionKey, config);
    }
  }
  return [...configsByAction.values()];
};

const removableRepeatableRootPaths = new Set(
  ADDABLE_REPEATABLE_PROFILE_SECTIONS.flatMap((config) => [
    config.rootPath,
    ...(config.fallbackRootPaths ?? []),
  ]),
);

const legacyExpenseRows = (payload: InvestorProfilePayload): unknown[] => {
  const legacyExpenses = valueAtProfilePath(payload, EXPENSE_REPEATABLE_ROOT_PATH);
  if (!isRecord(legacyExpenses)) return [];

  const legacyFields = [
    ['total_fixed_monthly', 'Fixed expenses', 'MONTHLY'],
    ['total_variable_monthly', 'Variable expenses', 'MONTHLY'],
    ['annual_one_time', 'One-time expenses', 'ONE_TIME'],
  ] as const;

  return legacyFields.flatMap(([fieldName, name, frequency]) => {
    const amount = legacyExpenses[fieldName];
    if (!isMoneyDraftValue(amount) || amount.amount === null || amount.amount === undefined) {
      return [];
    }
    return [{ name, amount: cloneProfileValue(amount), frequency }];
  });
};

const repeatableRowsForRootPath = (
  payload: InvestorProfilePayload,
  overrides: RepeatableRowOverridesByRootPath,
  rootPath: string,
): unknown[] => {
  const overriddenRows = overrides[rootPath];
  if (overriddenRows) return overriddenRows;
  const payloadRows = valueAtProfilePath(payload, rootPath);
  if (rootPath === EXPENSE_REPEATABLE_ROOT_PATH && isRecord(payloadRows)) {
    return legacyExpenseRows(payload);
  }
  return Array.isArray(payloadRows) ? payloadRows : [];
};

const payloadWithRepeatableRowOverrides = (
  payload: InvestorProfilePayload,
  rowOverridesByRootPath: RepeatableRowOverridesByRootPath,
): InvestorProfilePayload => {
  if (Object.keys(rowOverridesByRootPath).length === 0) return payload;

  const nextPayload = cloneProfileValue(payload) as InvestorProfilePayload;
  for (const [rootPath, rows] of Object.entries(rowOverridesByRootPath)) {
    setValueAtProfilePath(nextPayload, rootPath, cloneProfileValue(rows));
  }

  return nextPayload;
};

const profileJsonValuesEqual = (left: unknown, right: unknown): boolean =>
  JSON.stringify(left) === JSON.stringify(right);

const changedRepeatableRootPaths = (
  payload: InvestorProfilePayload,
  rowOverridesByRootPath: RepeatableRowOverridesByRootPath,
): string[] =>
  Object.entries(rowOverridesByRootPath)
    .filter(
      ([rootPath, rows]) =>
        !profileJsonValuesEqual(valueAtProfilePath(payload, rootPath) ?? [], rows),
    )
    .map(([rootPath]) => rootPath);

const missingDraftPropertyTypePath = ({
  basePayload,
  payload,
  rowOverridesByRootPath,
  values,
}: {
  basePayload: InvestorProfilePayload;
  payload: InvestorProfilePayload;
  rowOverridesByRootPath: RepeatableRowOverridesByRootPath;
  values: ProfilePayloadFormValues;
}): string | null => {
  const propertyRows = rowOverridesByRootPath[PROPERTY_REPEATABLE_ROOT_PATH];
  if (!propertyRows) return null;

  const properties = valueAtProfilePath(payload, PROPERTY_REPEATABLE_ROOT_PATH);
  if (!Array.isArray(properties)) return null;

  const firstDraftIndex = Array.isArray(
    valueAtProfilePath(basePayload, PROPERTY_REPEATABLE_ROOT_PATH),
  )
    ? (valueAtProfilePath(basePayload, PROPERTY_REPEATABLE_ROOT_PATH) as unknown[]).length
    : 0;
  if (properties.length <= firstDraftIndex) return null;

  for (let index = firstDraftIndex; index < properties.length; index += 1) {
    const fieldPath = `${PROPERTY_REPEATABLE_ROOT_PATH}.${index}.${PROPERTY_TYPE_FIELD_KEY}`;
    if (!values[fieldPath]) return fieldPath;
  }

  return null;
};

const duplicatePrimaryResidencePropertyTypePath = ({
  payload,
  values,
}: {
  payload: InvestorProfilePayload;
  values: ProfilePayloadFormValues;
}): string | null => {
  const properties = valueAtProfilePath(payload, PROPERTY_REPEATABLE_ROOT_PATH);
  if (!Array.isArray(properties)) return null;

  const primaryResidencePaths = properties
    .map((_, index) => `${PROPERTY_REPEATABLE_ROOT_PATH}.${index}.${PROPERTY_TYPE_FIELD_KEY}`)
    .filter((fieldPath) => values[fieldPath] === PRIMARY_RESIDENCE_PROPERTY_TYPE);

  return primaryResidencePaths.length > 1 ? primaryResidencePaths[1] : null;
};

const sanitizeRepeatableUpsertItem = (item: unknown): unknown => {
  if (!isRecord(item)) return item;
  return Object.fromEntries(
    Object.entries(item).filter(
      ([key, value]) =>
        !REPEATABLE_READ_ONLY_FIELD_KEYS.has(key) &&
        !(REPEATABLE_IDENTIFIER_FIELD_KEYS.has(key) && (value === null || value === undefined)),
    ),
  );
};

const componentPatchFromArray = (value: unknown): unknown =>
  Array.isArray(value) ? { upsert: value.map(sanitizeRepeatableUpsertItem) } : value;

const profileOwnedRepeatablePatchFromArray = (value: unknown): unknown =>
  Array.isArray(value) ? value.map(sanitizeRepeatableUpsertItem) : value;

const profilePayloadPatchWithRepeatableUpserts = (
  patch: ProfilePayloadPatch,
): ProfilePayloadPatch => {
  const update = { ...patch.update };

  if (Array.isArray(update.properties)) {
    update.properties = componentPatchFromArray(update.properties);
  }

  if (Array.isArray(update.businesses)) {
    const business = isRecord(update.business) ? update.business : {};
    update.business = {
      ...business,
      businesses: profileOwnedRepeatablePatchFromArray(update.businesses),
    };
    delete update.businesses;
  }

  if (isRecord(update.business) && Array.isArray(update.business.businesses)) {
    update.business = {
      ...update.business,
      businesses: profileOwnedRepeatablePatchFromArray(update.business.businesses),
    };
  }

  return { ...patch, update };
};

const withoutPropertyMortgageBalances = (
  payload: InvestorProfilePayload,
): InvestorProfilePayload => {
  const sections = payload.sections;
  if (!isRecord(sections) || !Array.isArray(sections.properties)) return payload;

  let changed = false;
  const properties = sections.properties.map((property) => {
    if (
      !isRecord(property) ||
      !Object.prototype.hasOwnProperty.call(property, 'mortgage_balance')
    ) {
      return property;
    }
    changed = true;
    const propertyWithoutMortgageBalance = { ...property };
    delete propertyWithoutMortgageBalance.mortgage_balance;
    return propertyWithoutMortgageBalance;
  });

  if (!changed) return payload;
  return {
    ...payload,
    sections: {
      ...sections,
      properties,
    },
  };
};

const hasEditablePrimaryResidenceAddress = (fieldMetadata: Record<string, unknown>): boolean => {
  const metadata = fieldMetadata[PRIMARY_RESIDENCE_ADDRESS_FIELD_PATH];
  return isRecord(metadata) && metadata.editable === true;
};

const editableProfilePayload = (payload: InvestorProfilePayload): InvestorProfilePayload => {
  if (!isRecord(payload.field_metadata)) return payload;
  const hideStandaloneResidenceState = hasEditablePrimaryResidenceAddress(payload.field_metadata);
  const citizenshipStatusMetadata = payload.field_metadata[CITIZENSHIP_STATUS_FIELD_PATH];
  const hasCitizenshipStatus =
    isRecord(citizenshipStatusMetadata) && citizenshipStatusMetadata.editable === true;

  const fieldMetadata = Object.fromEntries(
    Object.entries(payload.field_metadata)
      .filter(
        ([fieldPath]) =>
          !isProfilePortfolioFieldPath(fieldPath) &&
          !(hideStandaloneResidenceState && fieldPath === PRIMARY_RESIDENCE_STATE_FIELD_PATH) &&
          !(hasCitizenshipStatus && fieldPath === LEGACY_CITIZENSHIP_STATUS_FIELD_PATH),
      )
      .map(([fieldPath, metadata]) =>
        isBeneficiaryCandidateSourceStatusFieldPath(fieldPath)
          ? [fieldPath, hiddenProfilePayloadFieldMetadata(metadata)]
          : [fieldPath, metadata],
      ),
  );

  fieldMetadata[BENEFICIARY_CANDIDATE_SOURCE_STATUS_FIELD_PATH] = hiddenProfilePayloadFieldMetadata(
    payload.field_metadata[BENEFICIARY_CANDIDATE_SOURCE_STATUS_FIELD_PATH],
  );

  for (const fieldPath of PROFILE_PORTFOLIO_FIELD_PATTERNS) {
    fieldMetadata[fieldPath] = {
      editable: false,
      support_status: 'supported',
    };
  }

  return withoutPropertyMortgageBalances({
    ...payload,
    field_metadata: fieldMetadata,
  });
};

const isInvestorContactResidenceField = (fieldPath: string): boolean =>
  fieldPathMatchesAny(fieldPath, INVESTOR_CONTACT_RESIDENCE_FIELD_PATHS);

const isInvestorEmploymentField = (fieldPath: string): boolean =>
  fieldPathMatches(fieldPath, INVESTOR_EMPLOYMENT_FIELD_PATH);

const isSpouseEmploymentField = (fieldPath: string): boolean =>
  fieldPathMatches(fieldPath, SPOUSE_EMPLOYMENT_FIELD_PATH);

const citizenshipStatusForPayload = (
  payload: InvestorProfilePayload | null,
  values: ProfilePayloadFormValues,
): string => {
  const editedStatus =
    values[CITIZENSHIP_STATUS_FIELD_PATH] ?? values[LEGACY_CITIZENSHIP_STATUS_FIELD_PATH];
  if (editedStatus !== undefined) return editedStatus;
  if (!isRecord(payload?.sections)) return '';
  const investor = payload.sections.investor;
  if (!isRecord(investor)) return '';
  const status = investor.citizenship_status ?? investor.citizenship;
  return typeof status === 'string' ? status : '';
};

const isCitizenshipCountryFieldVisible = (fieldPath: string, status: string): boolean => {
  if (fieldPath === PRIMARY_CITIZENSHIP_COUNTRY_FIELD_PATH) {
    return ['PERMANENT_RESIDENT', 'NON_RESIDENT_ALIEN', 'DUAL_CITIZEN'].includes(status);
  }
  if (fieldPath === SECONDARY_CITIZENSHIP_COUNTRY_FIELD_PATH) {
    return status === 'DUAL_CITIZEN';
  }
  return true;
};

const hiddenCitizenshipCountryFieldPaths = (status: string): string[] =>
  [PRIMARY_CITIZENSHIP_COUNTRY_FIELD_PATH, SECONDARY_CITIZENSHIP_COUNTRY_FIELD_PATH].filter(
    (fieldPath) => !isCitizenshipCountryFieldVisible(fieldPath, status),
  );

const isSingleMaritalStatus = (value: unknown): boolean =>
  typeof value === 'string' && value.trim().toUpperCase() === 'SINGLE';

const sectionLabelForKey = (key: string): string => {
  const label = key.replace(/_/g, ' ').toLowerCase();
  return label.charAt(0).toUpperCase() + label.slice(1);
};

// Client-safe subset of the shared firm-data profile editor. The firm-data API
// owns portal visibility; this page only maps client-visible fields into sections.
const PROFILE_SECTIONS: ProfileSectionDefinition[] = [
  {
    id: 'personal-information',
    label: 'Personal information',
    matches: (field) =>
      fieldPathMatchesAny(field.path, [
        'sections.investor.name',
        'sections.investor.date_of_birth',
        'sections.investor.marital_status',
        'sections.investor.citizenship',
        'sections.investor.citizenship_status',
        PRIMARY_CITIZENSHIP_COUNTRY_FIELD_PATH,
        SECONDARY_CITIZENSHIP_COUNTRY_FIELD_PATH,
        'sections.investor.residency_status',
      ]) ||
      (fieldBelongsToSectionKey(field, ['investor']) &&
        !isInvestorContactResidenceField(field.path) &&
        !isInvestorEmploymentField(field.path)),
    fieldOrder: [
      'sections.investor.name.given_name',
      'sections.investor.name.middle_names',
      'sections.investor.name.family_name',
      'sections.investor.name.name_suffix',
      'sections.investor.date_of_birth',
      'sections.investor.marital_status',
      'sections.investor.citizenship',
      'sections.investor.citizenship_status',
      PRIMARY_CITIZENSHIP_COUNTRY_FIELD_PATH,
      SECONDARY_CITIZENSHIP_COUNTRY_FIELD_PATH,
      'sections.investor.residency_status',
    ],
  },
  {
    id: 'contact-residence',
    label: 'Contact & residence',
    matches: (field) => isInvestorContactResidenceField(field.path),
    fieldOrder: [
      'sections.investor.email',
      'sections.investor.phone_number',
      ...PRIMARY_RESIDENCE_ADDRESS_FIELD_ORDER,
      'sections.investor.primary_residence_state',
      ...MAILING_ADDRESS_FIELD_ORDER,
      'sections.investor.address',
    ],
  },
  {
    id: 'spouse',
    label: 'Spouse',
    matches: (field) =>
      fieldBelongsToSectionKey(field, ['spouse']) ||
      isSpouseEmploymentField(field.path) ||
      fieldPathMatches(field.path, SPOUSE_ANNUAL_INCOME_FIELD_PATH),
    fieldOrder: [
      'sections.spouse.name.given_name',
      'sections.spouse.name.middle_names',
      'sections.spouse.name.family_name',
      'sections.spouse.name.name_suffix',
      'sections.spouse.date_of_birth',
      'sections.spouse.relationship',
      'sections.spouse.employment.employment_status',
      'sections.spouse.employment.employer_name',
      'sections.spouse.employment.job_title',
      'sections.spouse.employment.started_on',
      'sections.spouse.employment.ended_on',
      SPOUSE_ANNUAL_INCOME_FIELD_PATH,
    ],
  },
  {
    id: 'household',
    label: 'Dependents',
    matches: (field) => fieldBelongsToSectionKey(field, ['dependents']),
    fieldOrder: DEPENDENT_FIELD_ORDER,
  },
  {
    id: 'employment',
    label: 'Employment',
    matches: (field) => isInvestorEmploymentField(field.path),
    fieldOrder: [
      'sections.investor.employment.employment_status',
      'sections.investor.employment.employer_name',
      'sections.investor.employment.job_title',
      'sections.investor.employment.started_on',
      'sections.investor.employment.ended_on',
    ],
  },
  {
    id: 'income',
    label: 'Income',
    description:
      'Add each client income source below. Mark a source as shared only when it also belongs to the spouse.',
    matches: (field) => fieldBelongsToSectionKey(field, ['income_sources']),
    fieldOrder: INCOME_SOURCE_FIELD_ORDER,
  },
  {
    id: 'expenses',
    label: 'Expenses',
    matches: (field) => fieldBelongsToSectionKey(field, ['expenses']),
    fieldOrder: EXPENSE_FIELD_ORDER,
  },
  {
    id: 'real-estate',
    label: 'Real estate',
    matches: (field) => fieldBelongsToSectionKey(field, ['properties']),
    fieldOrder: [
      'sections.properties[].property_type',
      'sections.properties[].address',
      'sections.properties[].current_value',
      'sections.properties[].estimated_value',
      'sections.properties[].joint_with_spouse',
      'sections.properties',
    ],
  },
  {
    id: 'business',
    label: 'Business',
    matches: (field) => fieldBelongsToSectionKey(field, ['business', 'businesses']),
    fieldOrder: BUSINESS_FIELD_ORDER,
  },
  {
    id: 'tax-profile',
    label: 'Tax profile',
    matches: (field) => fieldBelongsToSectionKey(field, ['tax']),
    fieldOrder: ['sections.tax'],
  },
  {
    id: 'goals',
    label: 'Goals',
    matches: (field) => fieldBelongsToSectionKey(field, ['goals']),
    fieldOrder: GOAL_FIELD_ORDER,
  },
  {
    id: 'retirement-risk',
    label: 'Retirement & risk',
    description: RETIREMENT_SECTION_DESCRIPTION,
    matches: (field) => fieldBelongsToSectionKey(field, ['retirement', 'risk']),
    fieldOrder: RETIREMENT_RISK_FIELD_ORDER,
  },
  {
    id: 'beneficiary-candidates',
    label: 'Beneficiary candidates',
    description: BENEFICIARY_CANDIDATE_SECTION_DESCRIPTION,
    matches: (field) => fieldBelongsToSectionKey(field, ['beneficiary_candidates']),
    fieldOrder: [
      'sections.beneficiary_candidates[].name.given_name',
      'sections.beneficiary_candidates[].name.middle_names',
      'sections.beneficiary_candidates[].name.family_name',
      'sections.beneficiary_candidates[].name.name_suffix',
      'sections.beneficiary_candidates[].relationship',
      'sections.beneficiary_candidates[].email',
      'sections.beneficiary_candidates[].phone_number',
      'sections.beneficiary_candidates[].last_verified_date',
      'sections.beneficiary_candidates[].notes',
      'sections.beneficiary_candidates',
    ],
  },
  {
    id: 'insurance',
    label: 'Insurance',
    matches: (field) => fieldBelongsToSectionKey(field, ['insurance']),
    fieldOrder: ['sections.insurance'],
  },
];

const fieldOrderRank = (field: ProfilePayloadInputField, fieldOrder: string[]): number => {
  const normalizedPath = field.path.replace(/\.\d+(?=\.|$)/g, '[]');
  const index = fieldOrder.findIndex(
    (pathPrefix) =>
      fieldPathMatches(field.path, pathPrefix) || fieldPathMatches(normalizedPath, pathPrefix),
  );
  return index === -1 ? fieldOrder.length : index;
};

const sortSectionFields = (
  fields: ProfilePayloadInputField[],
  fieldOrder: string[],
): ProfilePayloadInputField[] =>
  [...fields].sort((left, right) => {
    const leftRank = fieldOrderRank(left, fieldOrder);
    const rightRank = fieldOrderRank(right, fieldOrder);
    if (leftRank !== rightRank) return leftRank - rightRank;
    return left.path.localeCompare(right.path);
  });

const firstProfileSectionForField = (
  field: ProfilePayloadInputField,
): ProfileSectionDefinition | null =>
  PROFILE_SECTIONS.find((section) => section.matches(field)) ?? null;

const buildProfileSections = (fields: ProfilePayloadInputField[]): ProfileFieldSection[] => {
  const bySection = new Map<string, ProfilePayloadInputField[]>();
  const fallbackByKey = new Map<string, ProfilePayloadInputField[]>();

  for (const field of fields) {
    const section = firstProfileSectionForField(field);
    const sectionId = section?.id ?? sectionKeyForField(field);
    const targetMap = section ? bySection : fallbackByKey;
    const bucket = targetMap.get(sectionId) ?? [];
    bucket.push(field);
    targetMap.set(sectionId, bucket);
  }

  const sections: ProfileFieldSection[] = [];
  for (const section of PROFILE_SECTIONS) {
    const grouped = bySection.get(section.id) ?? [];
    if (grouped.length > 0) {
      sections.push({
        id: section.id,
        title: section.label,
        description: section.description,
        fields: sortSectionFields(grouped, section.fieldOrder),
      });
    }
  }

  for (const [key, grouped] of fallbackByKey.entries()) {
    if (grouped.length > 0) {
      sections.push({ id: key, title: sectionLabelForKey(key), fields: grouped });
    }
  }

  return sections;
};

const buildProfileSectionsWithEmptyAddableSections = ({
  fields,
  addableSectionIds,
  additionalSectionIds = new Set<string>(),
}: {
  fields: ProfilePayloadInputField[];
  addableSectionIds: Set<string>;
  additionalSectionIds?: Set<string>;
}): ProfileFieldSection[] => {
  const sections = buildProfileSections(fields);
  const presentSectionIds = new Set(sections.map((section) => section.id));

  for (const section of PROFILE_SECTIONS) {
    if (
      (!addableSectionIds.has(section.id) && !additionalSectionIds.has(section.id)) ||
      presentSectionIds.has(section.id)
    )
      continue;
    sections.push({
      id: section.id,
      title: section.label,
      description: section.description,
      fields: [],
    });
    presentSectionIds.add(section.id);
  }

  return sections.sort((left, right) => {
    const leftIndex = PROFILE_SECTIONS.findIndex((section) => section.id === left.id);
    const rightIndex = PROFILE_SECTIONS.findIndex((section) => section.id === right.id);
    const leftRank = leftIndex === -1 ? PROFILE_SECTIONS.length : leftIndex;
    const rightRank = rightIndex === -1 ? PROFILE_SECTIONS.length : rightIndex;
    return leftRank - rightRank;
  });
};

type ProfileSectionIndicators = {
  errors: number;
  warnings: number;
  missing: number;
  changed: number;
};

const emptyProfileSectionIndicators = (): ProfileSectionIndicators => ({
  errors: 0,
  warnings: 0,
  missing: 0,
  changed: 0,
});

const sectionIdForProfileField = (field: ProfilePayloadInputField): string =>
  firstProfileSectionForField(field)?.id ?? sectionKeyForField(field);

const profileSectionIndicators = ({
  fields,
  values,
  validationMessagesByPath,
}: {
  fields: ProfilePayloadInputField[];
  values: ProfilePayloadFormValues;
  validationMessagesByPath: Record<string, ProfilePayloadFieldValidationMessage[]>;
}): Map<string, ProfileSectionIndicators> => {
  const bySection = new Map<string, ProfileSectionIndicators>();

  for (const field of fields) {
    const sectionId = sectionIdForProfileField(field);
    const indicators = bySection.get(sectionId) ?? emptyProfileSectionIndicators();
    const currentValue = values[field.path] ?? '';
    const originalValue = isProfileSharedWithSpouseFieldPath(field.path)
      ? sharedWithSpouseSegmentedValue(field.value)
      : (field.value ?? '');

    if (field.required && !currentValue.trim()) indicators.missing += 1;
    if (currentValue !== originalValue) indicators.changed += 1;

    for (const message of validationMessagesByPath[field.path] ?? []) {
      if (message.severity === 'error') indicators.errors += 1;
      if (message.severity === 'warning') indicators.warnings += 1;
    }

    bySection.set(sectionId, indicators);
  }

  return bySection;
};

const profileSectionBadgeLabels = (indicators?: ProfileSectionIndicators): string[] => {
  if (!indicators) return [];
  const labels: string[] = [];
  if (indicators.errors > 0)
    labels.push(`${indicators.errors} error${indicators.errors === 1 ? '' : 's'}`);
  if (indicators.warnings > 0) {
    labels.push(`${indicators.warnings} warning${indicators.warnings === 1 ? '' : 's'}`);
  }
  if (indicators.missing > 0) labels.push(`${indicators.missing} missing`);
  if (indicators.changed > 0) labels.push(`${indicators.changed} unsaved`);
  return labels;
};

const formatProfileDateForAge = (value: string): Date | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

const profileAgeFromDateString = (value: string, now = new Date()): number | null => {
  const date = formatProfileDateForAge(value);
  if (!date || date.getTime() > now.getTime()) return null;
  let age = now.getFullYear() - date.getFullYear();
  const monthDiff = now.getMonth() - date.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < date.getDate())) {
    age -= 1;
  }
  return age;
};

const profileFieldHelperText = (field: ProfilePayloadInputField, value: string): string | null => {
  const normalizedPath = field.path.replace(/\.\d+(?=\.|$)/g, '[]');
  if (field.path.endsWith('.date_of_birth')) {
    const age = profileAgeFromDateString(value);
    return age === null ? null : `Age ${age}`;
  }
  if (
    normalizedPath === 'sections.goals[].target_amount' ||
    field.path === 'sections.retirement.desired_annual_spending'
  ) {
    return "Today's dollars.";
  }
  if (normalizedPath === 'sections.goals[].inflation_rate') {
    return 'Leave blank to use the plan default of 3%.';
  }
  return null;
};

const profileFieldSearchText = (
  field: ProfilePayloadInputField,
  values: ProfilePayloadFormValues,
): string =>
  [field.label, displayFieldLabel(field), field.path, values[field.path] ?? field.value ?? '']
    .join(' ')
    .toLowerCase();

const filterProfileSections = (
  sections: ProfileFieldSection[],
  searchTerm: string,
  values: ProfilePayloadFormValues,
): ProfileFieldSection[] => {
  const normalizedSearch = searchTerm.trim().toLowerCase();
  if (!normalizedSearch) return sections;

  return sections
    .map((section) => {
      const titleMatches = section.title.toLowerCase().includes(normalizedSearch);
      const fields = titleMatches
        ? section.fields
        : section.fields.filter((field) =>
            profileFieldSearchText(field, values).includes(normalizedSearch),
          );
      return fields.length > 0 || titleMatches ? { ...section, fields } : null;
    })
    .filter((section): section is ProfileFieldSection => Boolean(section));
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const formValuesAfterRemovingRepeatableRow = (
  values: ProfilePayloadFormValues,
  rootPath: string,
  removedIndex: number,
): ProfilePayloadFormValues => {
  const rootPattern = new RegExp(`^${escapeRegExp(rootPath)}\\.(\\d+)(\\..+)$`);
  const nextValues: ProfilePayloadFormValues = {};

  for (const [fieldPath, value] of Object.entries(values)) {
    const match = fieldPath.match(rootPattern);
    if (!match) {
      nextValues[fieldPath] = value;
      continue;
    }

    const fieldIndex = Number(match[1]);
    if (fieldIndex < removedIndex) {
      nextValues[fieldPath] = value;
      continue;
    }
    if (fieldIndex > removedIndex) {
      nextValues[`${rootPath}.${fieldIndex - 1}${match[2]}`] = value;
    }
  }

  return nextValues;
};

const slugForSubsectionId = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const repeatableDeleteNoun = (title: string): string =>
  `${title.charAt(0).toLowerCase()}${title.slice(1)}`;

const repeatableDeleteLabel = (title: string): string => `Delete ${repeatableDeleteNoun(title)}`;

const repeatableDeleteConfirmationTitle = (title: string): string =>
  `Are you sure you want to delete ${repeatableDeleteNoun(title)}?`;

const indexedSubsectionForRootPath = (
  fieldPath: string,
  rootPath: string,
  singularTitle: string,
): ProfileFieldSubsection | null => {
  const match = fieldPath.match(new RegExp(`^${escapeRegExp(rootPath)}\\.(\\d+)(?:\\.|$)`));
  if (!match) return null;

  const title = `${singularTitle} ${Number(match[1]) + 1}`;
  return {
    id: `${slugForSubsectionId(singularTitle)}-${match[1]}`,
    title,
    repeatableRootPath: rootPath,
    repeatableIndex: Number(match[1]),
  };
};

const repeatableSubsectionFromFieldLabel = (
  field: ProfilePayloadInputField,
): ProfileFieldSubsection | null => {
  const [firstSegment] = sectionRelativeFieldLabel(field.label).split(' / ');
  if (!/\d$/.test(firstSegment)) return null;

  return {
    id: slugForSubsectionId(firstSegment),
    title: firstSegment,
  };
};

const firstBusinessSubsection = (): ProfileFieldSubsection => ({
  id: 'business-0',
  title: 'Business 1',
});

const sectionHasBusinessRows = (fields: ProfilePayloadInputField[]): boolean =>
  fields.some(
    (field) =>
      indexedSubsectionForRootPath(field.path, BUSINESS_REPEATABLE_ROOT_PATH, 'Business') !==
        null ||
      indexedSubsectionForRootPath(field.path, LEGACY_BUSINESS_REPEATABLE_ROOT_PATH, 'Business') !==
        null,
  );

const subsectionForProfileField = (
  sectionId: string,
  field: ProfilePayloadInputField,
  sectionFields: ProfilePayloadInputField[] = [],
): ProfileFieldSubsection | null => {
  if (sectionId === 'contact-residence') {
    if (field.path.startsWith(MAILING_ADDRESS_FIELD_PREFIX)) {
      return {
        id: 'mailing-address',
        title: 'Mailing address',
        description: 'Only needed if it differs from the primary address.',
      };
    }
    if (
      field.path.startsWith(PRIMARY_RESIDENCE_ADDRESS_FIELD_PREFIX) ||
      field.path === PRIMARY_RESIDENCE_STATE_FIELD_PATH ||
      fieldPathMatches(field.path, 'sections.investor.address')
    ) {
      return {
        id: 'primary-residence',
        title: 'Primary residence',
      };
    }
    return {
      id: 'contact',
      title: 'Contact',
    };
  }

  if (sectionId === 'household') {
    return indexedSubsectionForRootPath(field.path, 'sections.dependents', 'Dependent');
  }

  if (sectionId === 'spouse') {
    if (isSpouseEmploymentField(field.path)) {
      return {
        id: 'spouse-employment',
        title: 'Spouse employment',
      };
    }
    if (fieldPathMatches(field.path, SPOUSE_ANNUAL_INCOME_FIELD_PATH)) {
      return {
        id: 'spouse-income',
        title: 'Spouse income',
        description:
          'Income earned by the spouse outside of client income sources marked as shared with the investor.',
      };
    }
    if (fieldPathMatches(field.path, 'sections.spouse')) {
      return {
        id: 'spouse-details',
        title: 'Spouse details',
      };
    }
  }

  if (sectionId === 'employment') {
    if (isInvestorEmploymentField(field.path)) {
      return {
        id: 'client-employment',
        title: 'Client employment',
      };
    }
  }

  if (sectionId === 'income') {
    return indexedSubsectionForRootPath(field.path, INCOME_REPEATABLE_ROOT_PATH, 'Income source');
  }

  if (sectionId === 'expenses') {
    return indexedSubsectionForRootPath(field.path, EXPENSE_REPEATABLE_ROOT_PATH, 'Expense');
  }

  if (sectionId === 'beneficiary-candidates') {
    return indexedSubsectionForRootPath(
      field.path,
      BENEFICIARY_CANDIDATE_REPEATABLE_ROOT_PATH,
      'Candidate',
    );
  }

  if (sectionId === 'real-estate') {
    return indexedSubsectionForRootPath(field.path, PROPERTY_REPEATABLE_ROOT_PATH, 'Property');
  }

  if (sectionId === 'goals') {
    return indexedSubsectionForRootPath(field.path, GOAL_REPEATABLE_ROOT_PATH, 'Goal');
  }

  if (sectionId === 'business') {
    if (field.path === 'sections.business.owns_business') {
      return sectionHasBusinessRows(sectionFields)
        ? firstBusinessSubsection()
        : {
            id: 'business-ownership',
            title: 'Business ownership',
          };
    }
    return (
      indexedSubsectionForRootPath(field.path, BUSINESS_REPEATABLE_ROOT_PATH, 'Business') ??
      indexedSubsectionForRootPath(field.path, LEGACY_BUSINESS_REPEATABLE_ROOT_PATH, 'Business') ??
      (fieldPathMatches(field.path, 'sections.business')
        ? {
            id: 'business-details',
            title: 'Business details',
          }
        : null)
    );
  }

  return repeatableSubsectionFromFieldLabel(field);
};

const profileFieldGroupsForSection = (section: ProfileFieldSection): ProfileFieldGroup[] => {
  const groups: ProfileFieldGroup[] = [];
  const groupsById = new Map<string, ProfileFieldGroup>();

  for (const field of section.fields) {
    const subsection = subsectionForProfileField(section.id, field, section.fields);
    const groupId = subsection?.id ?? '__ungrouped__';
    const existingGroup = groupsById.get(groupId);
    if (existingGroup) {
      existingGroup.fields.push(field);
      continue;
    }

    const group: ProfileFieldGroup = {
      id: groupId,
      title: subsection?.title,
      description: subsection?.description,
      repeatableRootPath: subsection?.repeatableRootPath,
      repeatableIndex: subsection?.repeatableIndex,
      fields: [field],
    };
    groupsById.set(groupId, group);
    groups.push(group);
  }

  if (section.id !== 'business') return groups;

  const businessOwnerField = section.fields.find((field) =>
    isBusinessOwnsBusinessFieldPath(field.path),
  );
  if (!businessOwnerField) return groups;

  return groups.map((group) => {
    if (
      !BUSINESS_ROW_GROUP_ID_PATTERN.test(group.id) ||
      group.fields.some((field) => isBusinessOwnsBusinessFieldPath(field.path))
    ) {
      return group;
    }

    const nameFieldIndex = group.fields.findIndex((field) => isBusinessNameFieldPath(field.path));
    const insertIndex = nameFieldIndex === -1 ? 0 : nameFieldIndex + 1;
    const fields = [...group.fields];
    fields.splice(insertIndex, 0, businessOwnerField);
    return { ...group, fields };
  });
};

const WealthProfile: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const identity = useAppSelector((state: RootState) => state.identity);
  const apexAccounts = useAppSelector(selectApexAccounts);
  const { enabled: firmDataEnabled, loading: firmDataLoading } = useFirmDataEnabled(
    user?.client_id,
  );

  const [formState, setFormState] = useState<ProfileFormState>(emptyFormState);
  const [riskScores, setRiskScores] = useState<RiskScoreRecord[]>([]);
  const [isRiskScoreLoading, setIsRiskScoreLoading] = useState(false);
  const riskScoreRequestGeneration = useRef(0);
  const [hasResolvedApexAccounts, setHasResolvedApexAccounts] = useState(
    () => apexAccounts.length > 0,
  );
  const [profilePayload, setProfilePayload] = useState<InvestorProfilePayload | null>(null);
  const [profilePayloadLoading, setProfilePayloadLoading] = useState(false);
  const [profilePayloadError, setProfilePayloadError] = useState<string | null>(null);
  const [profilePatchValues, setProfilePatchValues] = useState<ProfilePayloadFormValues>({});
  const [repeatableRowOverrides, setRepeatableRowOverrides] =
    useState<RepeatableRowOverridesByRootPath>({});
  const [profilePatchSaving, setProfilePatchSaving] = useState(false);
  const [profilePatchError, setProfilePatchError] = useState<string | null>(null);
  const [profilePatchStatus, setProfilePatchStatus] = useState<string | null>(null);
  const [profileSearchTerm, setProfileSearchTerm] = useState('');
  const [focusedMoneyFieldPath, setFocusedMoneyFieldPath] = useState<string | null>(null);
  const profileEditablePayload = useMemo(
    () => (profilePayload ? editableProfilePayload(profilePayload) : null),
    [profilePayload],
  );
  const profileFormPayload = useMemo(
    () =>
      profileEditablePayload
        ? payloadWithRepeatableRowOverrides(profileEditablePayload, repeatableRowOverrides)
        : null,
    [profileEditablePayload, repeatableRowOverrides],
  );
  const profileChangedRepeatableRootPaths = useMemo(
    () =>
      profileEditablePayload
        ? changedRepeatableRootPaths(profileEditablePayload, repeatableRowOverrides)
        : [],
    [profileEditablePayload, repeatableRowOverrides],
  );
  const profileAddableRepeatableSections = useMemo(
    () => (profileFormPayload ? addableRepeatableSectionsForPayload(profileFormPayload) : []),
    [profileFormPayload],
  );
  const profileAddableConfigsBySectionId = useMemo(() => {
    const configsBySectionId = new Map<string, AddableRepeatableProfileSection[]>();
    for (const config of profileAddableRepeatableSections) {
      const configs = configsBySectionId.get(config.sectionId) ?? [];
      configs.push(config);
      configsBySectionId.set(config.sectionId, configs);
    }
    return configsBySectionId;
  }, [profileAddableRepeatableSections]);
  const profileAddableSectionIds = useMemo(
    () => new Set(profileAddableRepeatableSections.map((section) => section.sectionId)),
    [profileAddableRepeatableSections],
  );
  const profilePatchCitizenshipStatus = useMemo(
    () =>
      profileFormPayload ? citizenshipStatusForPayload(profileFormPayload, profilePatchValues) : '',
    [profileFormPayload, profilePatchValues],
  );
  const profilePatchChangeFields = useMemo(() => {
    if (!profileFormPayload) return [];
    return getEditableProfilePayloadFields(profileFormPayload).filter((field) =>
      isCitizenshipCountryFieldVisible(field.path, profilePatchCitizenshipStatus),
    );
  }, [profileFormPayload, profilePatchCitizenshipStatus]);
  const profilePatchFields = useMemo(
    () => profilePatchChangeFields.filter((field) => !isHiddenProfileField(field.path)),
    [profilePatchChangeFields],
  );
  const profilePatchSaveDisabled =
    !profileFormPayload ||
    (profilePatchFields.length === 0 && profileChangedRepeatableRootPaths.length === 0);
  const isSpouseSectionDisabled = isSingleMaritalStatus(
    profilePatchValues[INVESTOR_MARITAL_STATUS_FIELD_PATH] ??
      valueAtProfilePath(profileFormPayload, INVESTOR_MARITAL_STATUS_FIELD_PATH),
  );
  const disabledProfileFieldPrefixes = useMemo(
    () => (isSpouseSectionDisabled ? [SPOUSE_SECTION_FIELD_PATH] : []),
    [isSpouseSectionDisabled],
  );
  const disabledProfileValidationFieldPrefixes = useMemo(
    () => [
      ...disabledProfileFieldPrefixes,
      ...hiddenCitizenshipCountryFieldPaths(profilePatchCitizenshipStatus),
    ],
    [disabledProfileFieldPrefixes, profilePatchCitizenshipStatus],
  );
  const profilePatchValidation = useMemo(
    () =>
      profileFormPayload
        ? validateProfilePayloadForm({
            payload: profileFormPayload,
            values: profilePatchValues,
            disabledFieldPrefixes: disabledProfileValidationFieldPrefixes,
          })
        : null,
    [disabledProfileValidationFieldPrefixes, profileFormPayload, profilePatchValues],
  );
  const profilePatchDisplayValidationMessagesByPath = useMemo(() => {
    const messagesByPath = profilePatchValidation?.messagesByPath ?? {};
    return Object.fromEntries(
      Object.entries(messagesByPath)
        .map(([path, messages]) => [
          path,
          messages.filter((message) => message.severity !== 'warning'),
        ])
        .filter(([, messages]) => messages.length > 0),
    );
  }, [profilePatchValidation?.messagesByPath]);
  const profileSections = useMemo(
    () =>
      profileFormPayload
        ? buildProfileSectionsWithEmptyAddableSections({
            fields: profilePatchFields,
            addableSectionIds: profileAddableSectionIds,
            additionalSectionIds: new Set(['spouse']),
          })
        : [],
    [profileAddableSectionIds, profileFormPayload, profilePatchFields],
  );
  const visibleProfileSections = useMemo(
    () => filterProfileSections(profileSections, profileSearchTerm, profilePatchValues),
    [profilePatchValues, profileSearchTerm, profileSections],
  );
  const profileSectionIndicatorsById = useMemo(
    () =>
      profileSectionIndicators({
        fields: profilePatchFields,
        values: profilePatchValues,
        validationMessagesByPath: profilePatchDisplayValidationMessagesByPath,
      }),
    [profilePatchDisplayValidationMessagesByPath, profilePatchFields, profilePatchValues],
  );
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const suppressSpyUntil = useRef<number>(0);
  const [activeSection, setActiveSection] = useState<string>('');

  const scrollToSection = (id: string) => {
    const el = sectionRefs.current[id];
    if (!el) return;
    suppressSpyUntil.current = Date.now() + 900;
    setActiveSection(id);
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const focusProfileField = (fieldPath: string) => {
    const field = fieldRefs.current[fieldPath];
    if (!field) return;
    field.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => {
      field
        .querySelector<HTMLElement>('input, textarea, button, [tabindex]:not([tabindex="-1"])')
        ?.focus();
    }, 120);
  };

  const clearSearchAndFocusProfileField = (fieldPath: string) => {
    if (profileSearchTerm.trim()) {
      setProfileSearchTerm('');
      window.setTimeout(() => focusProfileField(fieldPath), 0);
      return;
    }

    focusProfileField(fieldPath);
  };

  // Highlight the section the reader is currently on (scroll spy), scoped to the
  // page's own scroll container so the left nav tracks position.
  useEffect(() => {
    if (visibleProfileSections.length === 0 || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }
    const elements = visibleProfileSections
      .map((section) => sectionRefs.current[section.id])
      .filter((el): el is HTMLDivElement => Boolean(el));
    if (elements.length === 0) return undefined;

    setActiveSection((current) =>
      visibleProfileSections.some((section) => section.id === current)
        ? current
        : visibleProfileSections[0].id,
    );

    const scrollParent = (node: HTMLElement | null): HTMLElement | null => {
      let parent = node?.parentElement ?? null;
      while (parent) {
        const overflowY = getComputedStyle(parent).overflowY;
        if (
          (overflowY === 'auto' || overflowY === 'scroll') &&
          parent.scrollHeight > parent.clientHeight
        ) {
          return parent;
        }
        parent = parent.parentElement;
      }
      return null;
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (Date.now() < suppressSpyUntil.current) return;
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const id = (visible[0]?.target as HTMLElement | undefined)?.dataset.sectionId;
        if (id) setActiveSection(id);
      },
      { root: scrollParent(elements[0]), rootMargin: '-15% 0px -75% 0px', threshold: 0 },
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [visibleProfileSections]);

  const syncFormFromStore = () => {
    setFormState({
      fullName: user?.name ?? '',
      dateOfBirth: user?.date_of_birth ?? '',
    });
  };

  useEffect(() => {
    if (firmDataEnabled) return;
    syncFormFromStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmDataEnabled, user, identity]);

  useEffect(() => {
    if (!user?.userId) {
      return;
    }
    if (apexAccounts.length > 0) {
      setHasResolvedApexAccounts(true);
      return;
    }

    let cancelled = false;
    setHasResolvedApexAccounts(false);
    const request = dispatch(fetchApexAccounts()) as {
      unwrap?: () => Promise<unknown>;
    };
    if (typeof request?.unwrap !== 'function') {
      return () => {
        cancelled = true;
      };
    }

    void request.unwrap().then(
      () => {
        if (!cancelled) {
          setHasResolvedApexAccounts(true);
        }
      },
      () => {
        // A failed fetch is not "no accounts". Keep unresolved so we do not
        // show or save the profile-level template for an existing-account user.
      },
    );

    return () => {
      cancelled = true;
    };
  }, [apexAccounts.length, dispatch, user?.userId]);

  const riskScoreByAccountId = useMemo(() => {
    const scores = new Map<string, number>();
    for (const record of riskScores) {
      if (record.account_id) {
        scores.set(record.account_id, record.risk_score);
      }
    }
    return scores;
  }, [riskScores]);

  const initialRiskScore = useMemo(() => {
    const initial = riskScores.find((record) => record.account_id == null);
    return initial?.risk_score ?? 0;
  }, [riskScores]);

  const profileRiskCards = useMemo(() => {
    const accountCards = apexAccounts.map((account) => ({
      key: account.id,
      accountId: account.id as string | null,
      accountLabel: `${accountTypeToName(account.type)} (${account.name})`,
      score: riskScoreByAccountId.get(account.id) ?? 0,
    }));

    // Empty accounts is the default store state. Wait until Apex has loaded
    // successfully so a failed or in-flight fetch is not treated as "no
    // accounts".
    if (accountCards.length === 0 && !hasResolvedApexAccounts) {
      return [];
    }

    return [
      {
        key: 'initial',
        accountId: null as string | null,
        accountLabel: accountCards.length > 0 ? 'Profile' : undefined,
        score: initialRiskScore,
      },
      ...accountCards,
    ];
  }, [apexAccounts, hasResolvedApexAccounts, initialRiskScore, riskScoreByAccountId]);

  useEffect(() => {
    if (firmDataLoading) {
      return;
    }

    if ((!user?.userId && !user?.cognito_id) || !firmDataEnabled) {
      setProfilePayload(null);
      setProfilePatchValues({});
      setRepeatableRowOverrides({});
      setProfileSearchTerm('');
      return;
    }

    let isCurrentRequest = true;
    setProfilePayloadLoading(true);
    setProfilePayloadError(null);
    setProfilePatchError(null);
    setProfilePatchStatus(null);

    fetchInvestorProfileV1()
      .then((payload) => {
        if (!isCurrentRequest) return;
        setProfilePayload(payload);
        if (!payload) {
          setProfilePatchValues({});
          setRepeatableRowOverrides({});
          setProfileSearchTerm('');
          return;
        }

        setRepeatableRowOverrides({});
        setProfilePatchValues(
          defaultClientProfilePayloadFormValues(editableProfilePayload(payload)),
        );
        setProfileSearchTerm('');
      })
      .catch(() => {
        if (!isCurrentRequest) return;
        setProfilePayload(null);
        setProfilePatchValues({});
        setRepeatableRowOverrides({});
        setProfileSearchTerm('');
        setProfilePayloadError('Firm-data profile unavailable.');
      })
      .finally(() => {
        if (!isCurrentRequest) return;
        setProfilePayloadLoading(false);
      });

    return () => {
      isCurrentRequest = false;
    };
  }, [
    user?.cognito_id,
    user?.date_of_birth,
    user?.name,
    user?.userId,
    firmDataEnabled,
    firmDataLoading,
  ]);

  useEffect(() => {
    if (!user?.cognito_id || !user?.client_id) {
      setRiskScores([]);
      setIsRiskScoreLoading(false);
      return;
    }

    let isCurrentRequest = true;
    const requestGeneration = riskScoreRequestGeneration.current;
    setRiskScores([]);
    setIsRiskScoreLoading(true);

    const fetchRiskScore = async () => {
      try {
        const response = await getRiskScores({
          user_id: user.cognito_id,
          firm_id: user.client_id,
        });

        if (!isCurrentRequest) return;
        if (response.status !== 'success') return;

        const scoreData = Array.isArray(response.data)
          ? response.data
          : response.data
            ? [response.data]
            : [];
        const loadedScores = scoreData.filter(
          (record): record is RiskScoreRecord =>
            !!record &&
            typeof record.risk_score === 'number' &&
            Number.isFinite(record.risk_score),
        );

        if (riskScoreRequestGeneration.current !== requestGeneration) {
          setRiskScores((savedScores) => [
            ...savedScores,
            ...loadedScores.filter(
              (loaded) =>
                !savedScores.some(
                  (saved) => (saved.account_id ?? null) === (loaded.account_id ?? null),
                ),
            ),
          ]);
          return;
        }

        setRiskScores(loadedScores);
      } catch (error) {
        if (!isCurrentRequest) return;
        console.error('Failed to load risk score for profile page', error);
      } finally {
        if (isCurrentRequest) setIsRiskScoreLoading(false);
      }
    };

    void fetchRiskScore();

    return () => {
      isCurrentRequest = false;
    };
  }, [user?.client_id, user?.cognito_id]);

  useEffect(() => {
    const state = location.state as {
      retakeRiskScore?: number;
      accountId?: string | null;
    } | null;

    if (!state?.retakeRiskScore) return;

    if (!user?.cognito_id || !user?.client_id) {
      return;
    }

    if (!hasResolvedApexAccounts) {
      return;
    }

    const targetAccountId = state.accountId ?? null;
    if (targetAccountId != null && apexAccounts.length === 0) {
      return;
    }

    const applyRetakeScore = async () => {
      const roundedScore = Math.max(1, Math.round(state.retakeRiskScore!));
      try {
        await updateRiskScore({
          user_id: user.cognito_id,
          firm_id: user.client_id,
          account_id: targetAccountId,
          risk_score: roundedScore,
        });
        notifyRiskScoresChanged();
        riskScoreRequestGeneration.current += 1;
        setRiskScores((prev) => {
          const existing = prev.find((record) =>
            targetAccountId == null
              ? record.account_id == null
              : record.account_id === targetAccountId,
          );
          const withoutTarget = prev.filter((record) =>
            targetAccountId == null
              ? record.account_id != null
              : record.account_id !== targetAccountId,
          );
          return [
            ...withoutTarget,
            {
              id: existing?.id ?? `local-${targetAccountId ?? 'initial'}`,
              user_id: user.cognito_id,
              firm_id: user.client_id,
              account_id: targetAccountId,
              risk_score: roundedScore,
            },
          ];
        });
        navigate(location.pathname, { replace: true, state: {} });
      } catch (error) {
        console.error('Failed to update retake risk score from profile page', error);
      }
    };

    void applyRetakeScore();
  }, [
    apexAccounts.length,
    hasResolvedApexAccounts,
    location.pathname,
    location.state,
    navigate,
    user?.client_id,
    user?.cognito_id,
  ]);

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '—';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatAddressFromIdentity = () => {
    const addr = identity?.personalAddress;
    if (!addr) return '—';
    const parts = [addr.line1, addr.line2, addr.city, addr.state, addr.postalCode].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : '—';
  };

  const displayFields = firmDataEnabled
    ? []
    : (() => {
        const investorSection = getInvestorSection(profilePayload);
        const spouseSection = getSpouseSection(profilePayload);
        const taxSection = getTaxSection(profilePayload);
        const firstName = getNamePart(investorSection, 'name', 'given_name');
        const lastName = getNamePart(investorSection, 'name', 'family_name');

        return [
          ...(firstName || lastName
            ? [
                { label: 'First name', value: firstName ?? '—' },
                { label: 'Last name', value: lastName ?? '—' },
              ]
            : [
                {
                  label: 'Full legal name',
                  value: getStringField(investorSection, 'name') || formState.fullName || '—',
                },
              ]),
          {
            label: 'Email address',
            value: getStringField(investorSection, 'email') || user?.email || '—',
          },
          {
            label: 'Date of birth',
            value: formatDate(
              getStringField(investorSection, 'date_of_birth') || formState.dateOfBirth,
            ),
          },
          {
            label: 'Marital status',
            value: formatProfileChoice(getStringField(investorSection, 'marital_status')),
          },
          {
            label: 'Tax filing status',
            value: formatProfileChoice(getStringField(taxSection, 'filing_status')),
          },
          {
            label: 'State of domicile',
            value: getStringField(investorSection, 'primary_residence_state') || '—',
          },
          {
            label: 'Primary residence address',
            value: getAddressDisplay(investorSection) || formatAddressFromIdentity(),
          },
          {
            label: 'Spouse first name',
            value: getNamePart(spouseSection, 'name', 'given_name') || '—',
          },
          {
            label: 'Spouse last name',
            value: getNamePart(spouseSection, 'name', 'family_name') || '—',
          },
        ];
      })();

  const handleProfilePatchSave = async () => {
    if (!profileFormPayload || !profileEditablePayload) return;

    const validation = validateProfilePayloadForm({
      payload: profileFormPayload,
      values: profilePatchValues,
      disabledFieldPrefixes: disabledProfileValidationFieldPrefixes,
    });
    if (validation.hasErrors) {
      setProfilePatchError('Fix the highlighted profile fields before saving.');
      setProfilePatchStatus(null);
      if (validation.firstErrorPath) clearSearchAndFocusProfileField(validation.firstErrorPath);
      return;
    }

    const missingPropertyTypePath = missingDraftPropertyTypePath({
      basePayload: profileEditablePayload,
      payload: profileFormPayload,
      rowOverridesByRootPath: repeatableRowOverrides,
      values: profilePatchValues,
    });
    if (missingPropertyTypePath) {
      setProfilePatchError(
        'Choose a property type for each added property so the primary residence is identified.',
      );
      setProfilePatchStatus(null);
      clearSearchAndFocusProfileField(missingPropertyTypePath);
      return;
    }

    const duplicatePrimaryResidencePath = duplicatePrimaryResidencePropertyTypePath({
      payload: profileFormPayload,
      values: profilePatchValues,
    });
    if (duplicatePrimaryResidencePath) {
      setProfilePatchError('Only one property can be marked as the primary residence.');
      setProfilePatchStatus(null);
      clearSearchAndFocusProfileField(duplicatePrimaryResidencePath);
      return;
    }

    let patchResult: ReturnType<typeof buildProfilePayloadPatch>;
    try {
      patchResult = buildProfilePayloadPatch({
        payload: profileFormPayload,
        values: profilePatchValues,
        idempotencyKey: `investor-profile-payload-${user?.userId ?? user?.cognito_id ?? 'investor'}-${Date.now()}`,
        changedRootPaths: profileChangedRepeatableRootPaths,
        disabledFieldPrefixes: disabledProfileFieldPrefixes,
        validationDisabledFieldPrefixes: disabledProfileValidationFieldPrefixes,
      });
    } catch (error) {
      setProfilePatchError(error instanceof Error ? error.message : 'Invalid profile update.');
      setProfilePatchStatus(null);
      return;
    }

    setProfilePatchSaving(true);
    setProfilePatchError(null);
    setProfilePatchStatus(null);

    try {
      const result = await updateInvestorProfileV1(
        profilePayloadPatchWithRepeatableUpserts(patchResult.patch),
      );
      setProfilePayload(result.profile);
      setRepeatableRowOverrides({});
      setProfilePatchValues(
        defaultClientProfilePayloadFormValues(editableProfilePayload(result.profile)),
      );
      setProfilePatchStatus('Profile updated.');
    } catch (error) {
      setProfilePatchError(
        error instanceof Error ? error.message : 'Failed to update firm-data profile.',
      );
    } finally {
      setProfilePatchSaving(false);
    }
  };

  const updateProfilePatchValue = (fieldPath: string, value: string) => {
    setProfilePatchValues((currentValues) => {
      const nextValues = {
        ...currentValues,
        [fieldPath]: value,
      };
      if (
        fieldPath === CITIZENSHIP_STATUS_FIELD_PATH ||
        fieldPath === LEGACY_CITIZENSHIP_STATUS_FIELD_PATH
      ) {
        if (!isCitizenshipCountryFieldVisible(PRIMARY_CITIZENSHIP_COUNTRY_FIELD_PATH, value)) {
          nextValues[PRIMARY_CITIZENSHIP_COUNTRY_FIELD_PATH] = '';
        }
        if (!isCitizenshipCountryFieldVisible(SECONDARY_CITIZENSHIP_COUNTRY_FIELD_PATH, value)) {
          nextValues[SECONDARY_CITIZENSHIP_COUNTRY_FIELD_PATH] = '';
        }
      }
      return nextValues;
    });
    setProfilePatchError(null);
    setProfilePatchStatus(null);
  };

  const addRepeatableProfileRow = (rootPath: string) => {
    if (!profileEditablePayload) return;
    const config = ADDABLE_REPEATABLE_PROFILE_SECTIONS.find(
      (candidate) => candidate.rootPath === rootPath,
    );
    if (!config) return;

    setRepeatableRowOverrides((currentRowsByRootPath) => {
      const currentRows = repeatableRowsForRootPath(
        profileEditablePayload,
        currentRowsByRootPath,
        rootPath,
      );
      return {
        ...currentRowsByRootPath,
        [rootPath]: [...currentRows, config.draftItem(profileEditablePayload)],
      };
    });
    setProfilePatchError(null);
    setProfilePatchStatus(null);
  };

  const removeRepeatableProfileRow = (rootPath: string, index: number) => {
    if (!profileEditablePayload) return;

    setRepeatableRowOverrides((currentRowsByRootPath) => {
      const currentRows = repeatableRowsForRootPath(
        profileEditablePayload,
        currentRowsByRootPath,
        rootPath,
      );
      if (index < 0 || index >= currentRows.length) return currentRowsByRootPath;

      return {
        ...currentRowsByRootPath,
        [rootPath]: currentRows.filter((_, rowIndex) => rowIndex !== index),
      };
    });
    setProfilePatchValues((currentValues) =>
      formValuesAfterRemovingRepeatableRow(currentValues, rootPath, index),
    );
    setProfilePatchError(null);
    setProfilePatchStatus(null);
  };

  if (firmDataLoading) {
    return (
      <div className="wealth-profile-page">
        <div
          aria-label="Loading firm data settings"
          style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="wealth-profile-page">
      <div className="wealth-profile-grid">
        {!firmDataEnabled ? (
          <div className="wealth-profile-card">
            <div className="section-header">
              <h2 className="section-title">Personal information</h2>
            </div>

            <div className="fields-list">
              {displayFields.map(({ label, value }) => (
                <div className="field-row" key={label}>
                  <span className="field-label">{label}</span>
                  <span className="field-value">{value}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="flex flex-col gap-4">
          {profileRiskCards.length === 0 ? (
            <RiskProfile score={0} isLoading lastUpdated="Oct 26, 2023" />
          ) : (
            profileRiskCards.map((card) => (
              <RiskProfile
                key={card.key}
                score={card.score}
                isLoading={isRiskScoreLoading}
                lastUpdated="Oct 26, 2023"
                accountId={card.accountId}
                accountLabel={card.accountLabel}
              />
            ))
          )}
        </div>
      </div>

      {firmDataEnabled ? (
        <section className="wealth-profile-payload-panel" aria-label="Investment profile">
          <div className="section-header">
            <h2 className="section-title">Investment Profile</h2>
          </div>

          {profilePayloadLoading ? (
            <div
              className="profile-payload-loading-layout"
              role="status"
              aria-label="Loading profile"
            >
              <div className="profile-payload-loading-nav-placeholder" aria-hidden />
              <div className="profile-payload-loading">
                <Spin /> <span>Loading profile...</span>
              </div>
            </div>
          ) : null}
          {profilePayloadError ? (
            <div className="profile-payload-error" role="alert">
              {profilePayloadError}
            </div>
          ) : null}

          {profilePayload ? (
            <>
              {profileSections.length > 0 ? (
                <div className="profile-payload-toolbar">
                  <Input
                    allowClear
                    aria-label="Search profile fields"
                    className="profile-payload-search"
                    prefix={<SearchOutlined />}
                    placeholder="Search profile fields"
                    value={profileSearchTerm}
                    onChange={(event) => setProfileSearchTerm(event.target.value)}
                  />
                </div>
              ) : null}

              <div className="profile-payload-layout">
                {visibleProfileSections.length > 0 ? (
                  <nav className="profile-nav" aria-label="Profile sections">
                    {visibleProfileSections.map((section) => {
                      const badgeLabels = profileSectionBadgeLabels(
                        profileSectionIndicatorsById.get(section.id),
                      );
                      return (
                        <button
                          type="button"
                          key={section.id}
                          className={
                            'profile-nav-item' + (activeSection === section.id ? ' is-active' : '')
                          }
                          aria-current={activeSection === section.id ? 'true' : undefined}
                          onClick={() => scrollToSection(section.id)}
                        >
                          <span className="profile-nav-label">{section.title}</span>
                          {badgeLabels.length > 0 ? (
                            <span className="profile-nav-badges" aria-hidden="true">
                              {badgeLabels.map((label) => (
                                <span key={label} className="profile-nav-badge">
                                  {label}
                                </span>
                              ))}
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </nav>
                ) : null}

                <div className="profile-payload-editor">
                  {visibleProfileSections.length > 0 ? (
                    <div
                      className="profile-payload-sections"
                      data-testid="investment-profile-fields"
                    >
                      {visibleProfileSections.map((section) => {
                        const addableConfigs =
                          profileAddableConfigsBySectionId.get(section.id) ?? [];
                        const fieldGroups = profileFieldGroupsForSection(section);
                        const isIncomeSection = section.id === 'income';
                        const isBusinessSection = section.id === 'business';
                        const isExpenseSection = section.id === 'expenses';
                        const sectionDisabled = section.id === 'spouse' && isSpouseSectionDisabled;
                        return (
                          <div
                            key={section.id}
                            data-section-id={section.id}
                            ref={(el) => {
                              sectionRefs.current[section.id] = el;
                            }}
                            className="profile-payload-section"
                          >
                            <Card
                              title={
                                <div className="profile-payload-card-title">
                                  <span>{section.title}</span>
                                  {addableConfigs.length > 0 ? (
                                    <div className="profile-payload-card-actions">
                                      {addableConfigs.map((addableConfig) => (
                                        <Button
                                          key={addableConfig.rootPath}
                                          size="small"
                                          icon={<PlusOutlined />}
                                          className="profile-payload-add-button"
                                          onClick={() =>
                                            addRepeatableProfileRow(addableConfig.rootPath)
                                          }
                                        >
                                          {addableConfig.buttonLabel}
                                        </Button>
                                      ))}
                                    </div>
                                  ) : null}
                                </div>
                              }
                              className={
                                'profile-payload-card' + (sectionDisabled ? ' is-disabled' : '')
                              }
                              aria-disabled={sectionDisabled}
                            >
                              {section.description ? (
                                <p className="profile-payload-card-description">
                                  {section.description}
                                </p>
                              ) : null}
                              {sectionDisabled ? (
                                <div className="profile-payload-disabled-message" role="note">
                                  Spouse details are minimized because the investor is marked as
                                  single.
                                </div>
                              ) : (
                                <div
                                  className={
                                    'profile-payload-subsections' +
                                    (isIncomeSection ? ' is-income-table' : '') +
                                    (isBusinessSection ? ' is-business-table' : '') +
                                    (isExpenseSection ? ' is-expense-table' : '')
                                  }
                                >
                                  {isIncomeSection && fieldGroups.length > 0 ? (
                                    <div className="profile-income-table-header" aria-hidden="true">
                                      <span>Name</span>
                                      <span>Type</span>
                                      <span>Amount</span>
                                      <span>Frequency</span>
                                      <span>Shared</span>
                                      <span />
                                    </div>
                                  ) : null}
                                  {isBusinessSection && fieldGroups.length > 0 ? (
                                    <div
                                      className="profile-business-table-header"
                                      aria-hidden="true"
                                    >
                                      <span>Name</span>
                                      <span>Owner</span>
                                      <span>Estimated value</span>
                                      <span>Shared</span>
                                      <span />
                                    </div>
                                  ) : null}
                                  {isExpenseSection && fieldGroups.length > 0 ? (
                                    <div
                                      className="profile-expense-table-header"
                                      aria-hidden="true"
                                    >
                                      <span>Name</span>
                                      <span>Amount</span>
                                      <span>Frequency</span>
                                      <span />
                                    </div>
                                  ) : null}
                                  {fieldGroups.map((group) => {
                                    const subsection =
                                      group.title !== undefined
                                        ? {
                                            id: group.id,
                                            title: group.title,
                                            description: group.description,
                                          }
                                        : undefined;
                                    const removableRootPath =
                                      group.repeatableRootPath &&
                                      group.repeatableIndex !== undefined &&
                                      removableRepeatableRootPaths.has(group.repeatableRootPath)
                                        ? group.repeatableRootPath
                                        : null;
                                    const repeatableRow =
                                      removableRootPath && group.repeatableIndex !== undefined
                                        ? repeatableRowsForRootPath(
                                            profileFormPayload,
                                            {},
                                            removableRootPath,
                                          )[group.repeatableIndex]
                                        : undefined;
                                    const deleteRequiresConfirmation =
                                      removableRootPath !== null &&
                                      repeatableGroupHasUserData(
                                        group,
                                        repeatableRow,
                                        profilePatchValues,
                                      );
                                    const isCompactExpenseGroup =
                                      section.id === 'expenses' &&
                                      group.repeatableRootPath === EXPENSE_REPEATABLE_ROOT_PATH;
                                    const isCompactIncomeGroup =
                                      section.id === 'income' &&
                                      group.repeatableRootPath === INCOME_REPEATABLE_ROOT_PATH;
                                    const isCompactBusinessGroup =
                                      section.id === 'business' &&
                                      (group.repeatableRootPath === BUSINESS_REPEATABLE_ROOT_PATH ||
                                        group.repeatableRootPath ===
                                          LEGACY_BUSINESS_REPEATABLE_ROOT_PATH);
                                    const isCompactProfileGroup =
                                      isCompactIncomeGroup ||
                                      isCompactBusinessGroup ||
                                      isCompactExpenseGroup;
                                    const compactExpenseHasFrequencyField =
                                      isCompactExpenseGroup &&
                                      group.fields.some((field) =>
                                        isExpenseFrequencyFieldPath(field.path),
                                      );
                                    const showSubsectionTitle =
                                      group.title !== undefined &&
                                      group.id !== 'client-employment' &&
                                      !isCompactProfileGroup;
                                    const deleteControl = (() => {
                                      if (!removableRootPath) return null;
                                      const title = group.title ?? 'entry';
                                      const label = repeatableDeleteLabel(title);
                                      const removeRow = () =>
                                        removeRepeatableProfileRow(
                                          removableRootPath,
                                          group.repeatableIndex ?? -1,
                                        );
                                      const button = (
                                        <Button
                                          type="text"
                                          danger
                                          size="small"
                                          icon={<DeleteOutlined />}
                                          aria-label={label}
                                          title={label}
                                          className="profile-payload-subsection-remove"
                                          onClick={
                                            deleteRequiresConfirmation ? undefined : removeRow
                                          }
                                        />
                                      );

                                      if (!deleteRequiresConfirmation) return button;

                                      return (
                                        <Popconfirm
                                          title={repeatableDeleteConfirmationTitle(title)}
                                          description="This section has profile details. Deleting it will remove that information from this profile."
                                          okText="Delete"
                                          cancelText="Cancel"
                                          okButtonProps={{ danger: true }}
                                          onConfirm={removeRow}
                                        >
                                          {button}
                                        </Popconfirm>
                                      );
                                    })();
                                    return (
                                      <div
                                        key={group.id}
                                        className={
                                          'profile-payload-subsection' +
                                          (showSubsectionTitle ? ' has-title' : '') +
                                          (isCompactIncomeGroup
                                            ? ' is-compact-income profile-income-table-row'
                                            : '') +
                                          (isCompactBusinessGroup
                                            ? ' is-compact-business profile-business-table-row'
                                            : '') +
                                          (isCompactExpenseGroup
                                            ? ' is-compact-expense profile-expense-table-row'
                                            : '') +
                                          (compactExpenseHasFrequencyField
                                            ? ' has-expense-frequency-field'
                                            : '')
                                        }
                                      >
                                        {showSubsectionTitle ? (
                                          <div className="profile-payload-subsection-heading">
                                            <div className="profile-payload-subsection-heading-text">
                                              <span className="profile-payload-subsection-title">
                                                {group.title}
                                              </span>
                                              {group.description ? (
                                                <span className="profile-payload-subsection-description">
                                                  {group.description}
                                                </span>
                                              ) : null}
                                            </div>
                                            {deleteControl}
                                          </div>
                                        ) : null}
                                        <Row
                                          gutter={[16, isCompactProfileGroup ? 16 : 4]}
                                          className={
                                            isCompactIncomeGroup
                                              ? 'profile-compact-income-row'
                                              : isCompactBusinessGroup
                                                ? 'profile-compact-business-row'
                                                : isCompactExpenseGroup
                                                  ? 'profile-compact-expense-row'
                                                  : undefined
                                          }
                                        >
                                          {group.fields.map((field) => {
                                            const selectOptions = profileSelectOptions(field);
                                            const validationMessages =
                                              profilePatchDisplayValidationMessagesByPath[
                                                field.path
                                              ] ?? [];
                                            const status = profileFieldStatus(validationMessages);
                                            const fieldId = profileFieldDomId(field.path);
                                            const messageId =
                                              validationMessages.length > 0
                                                ? `${fieldId}-messages`
                                                : undefined;
                                            const ariaLabel = fieldAriaLabel(field, subsection);
                                            const fieldHelperText = profileFieldHelperText(
                                              field,
                                              profilePatchValues[field.path] ?? field.value ?? '',
                                            );
                                            const fieldValue =
                                              profilePatchValues[field.path] ?? field.value ?? '';
                                            const sharedWithSpouseValue =
                                              sharedWithSpouseSegmentedValue(fieldValue);
                                            const sharedWithSpouseControl =
                                              isProfileSharedWithSpouseFieldPath(field.path) ? (
                                                <Segmented
                                                  aria-label={ariaLabel}
                                                  className="profile-shared-with-spouse-toggle"
                                                  options={sharedWithSpouseOptionsForValue(
                                                    sharedWithSpouseValue,
                                                  )}
                                                  value={sharedWithSpouseValue}
                                                  onChange={(value) =>
                                                    updateProfilePatchValue(
                                                      field.path,
                                                      String(value),
                                                    )
                                                  }
                                                />
                                              ) : null;
                                            const isMoneyField = field.valueKind === 'money';
                                            const isPercentField = field.valueKind === 'percent';
                                            const renderedFieldValue =
                                              isMoneyField && focusedMoneyFieldPath !== field.path
                                                ? formattedMoneyFieldValue(fieldValue)
                                                : fieldValue;
                                            const isTextArea = isProfileTextAreaField(field);
                                            const amountTimingFrequencyPath =
                                              frequencyPathForTimedAmountPath(field.path);
                                            const selectedAmountTimingValue =
                                              amountTimingFrequencyPath &&
                                              profilePatchChangeFields.some(
                                                (changeField) =>
                                                  changeField.path === amountTimingFrequencyPath,
                                              )
                                                ? amountTimingValue(
                                                    amountTimingFrequencyPath,
                                                    profilePatchValues[amountTimingFrequencyPath] ??
                                                      valueAtProfilePath(
                                                        profileFormPayload,
                                                        amountTimingFrequencyPath,
                                                      ),
                                                  )
                                                : null;
                                            const inputControl = (
                                              <Input
                                                id={fieldId}
                                                aria-label={ariaLabel}
                                                type={
                                                  isMoneyField ? 'text' : profileInputType(field)
                                                }
                                                inputMode={
                                                  isMoneyField ? 'decimal' : profileInputMode(field)
                                                }
                                                suffix={isPercentField ? '%' : undefined}
                                                value={renderedFieldValue}
                                                placeholder={
                                                  field.required ? 'Required' : 'Not set'
                                                }
                                                status={status}
                                                aria-invalid={status === 'error' ? true : undefined}
                                                aria-describedby={messageId}
                                                onFocus={() => {
                                                  if (isMoneyField)
                                                    setFocusedMoneyFieldPath(field.path);
                                                }}
                                                onBlur={() => {
                                                  if (isMoneyField) setFocusedMoneyFieldPath(null);
                                                }}
                                                onChange={(event) =>
                                                  updateProfilePatchValue(
                                                    field.path,
                                                    isMoneyField
                                                      ? unformattedMoneyFieldValue(
                                                          event.target.value,
                                                        )
                                                      : event.target.value,
                                                  )
                                                }
                                              />
                                            );
                                            const amountTimingControl =
                                              selectedAmountTimingValue ? (
                                                <Segmented
                                                  aria-label={`${ariaLabel} frequency`}
                                                  className="profile-amount-timing"
                                                  options={amountTimingOptionsForFieldPath(
                                                    amountTimingFrequencyPath ?? '',
                                                  )}
                                                  value={selectedAmountTimingValue}
                                                  onChange={(value) => {
                                                    if (!amountTimingFrequencyPath) return;
                                                    updateProfilePatchValue(
                                                      amountTimingFrequencyPath,
                                                      String(value),
                                                    );
                                                  }}
                                                />
                                              ) : null;
                                            const renderCompactTimingCell =
                                              ((isCompactIncomeGroup &&
                                                isIncomeSourceAmountFieldPath(field.path)) ||
                                                (isCompactExpenseGroup &&
                                                  isExpenseAmountFieldPath(field.path))) &&
                                              Boolean(amountTimingControl);
                                            const renderedInputControl =
                                              amountTimingControl && !renderCompactTimingCell ? (
                                                <div className="profile-timed-amount-row">
                                                  {inputControl}
                                                  {amountTimingControl}
                                                </div>
                                              ) : (
                                                inputControl
                                              );
                                            return (
                                              <Fragment key={field.path}>
                                                <Col
                                                  {...profileFieldColumnProps({
                                                    hasAmountTimingControl:
                                                      Boolean(amountTimingControl),
                                                    isTextArea,
                                                  })}
                                                  className={
                                                    isCompactIncomeGroup
                                                      ? compactIncomeFieldClassName(field.path)
                                                      : isCompactBusinessGroup
                                                        ? compactBusinessFieldClassName(field.path)
                                                        : isCompactExpenseGroup
                                                          ? compactExpenseFieldClassName(field.path)
                                                          : undefined
                                                  }
                                                >
                                                  <div
                                                    className={
                                                      'profile-payload-field-shell' +
                                                      (status ? ` has-${status}` : '')
                                                    }
                                                    ref={(el) => {
                                                      fieldRefs.current[field.path] = el;
                                                    }}
                                                  >
                                                    <label className="profile-payload-field">
                                                      <span>
                                                        {displayFieldLabel(field, subsection)}
                                                      </span>
                                                      {sharedWithSpouseControl ? (
                                                        sharedWithSpouseControl
                                                      ) : selectOptions ? (
                                                        <Select
                                                          id={fieldId}
                                                          aria-label={ariaLabel}
                                                          value={fieldValue}
                                                          onChange={(value) =>
                                                            updateProfilePatchValue(
                                                              field.path,
                                                              value,
                                                            )
                                                          }
                                                          options={selectOptions}
                                                          status={status}
                                                          aria-invalid={
                                                            status === 'error' ? true : undefined
                                                          }
                                                          aria-describedby={messageId}
                                                        />
                                                      ) : isTextArea ? (
                                                        <Input.TextArea
                                                          id={fieldId}
                                                          className="profile-payload-notes-textarea"
                                                          aria-label={ariaLabel}
                                                          value={fieldValue}
                                                          placeholder={
                                                            field.required ? 'Required' : 'Not set'
                                                          }
                                                          rows={3}
                                                          status={status}
                                                          aria-invalid={
                                                            status === 'error' ? true : undefined
                                                          }
                                                          aria-describedby={messageId}
                                                          onChange={(event) =>
                                                            updateProfilePatchValue(
                                                              field.path,
                                                              event.target.value,
                                                            )
                                                          }
                                                        />
                                                      ) : (
                                                        renderedInputControl
                                                      )}
                                                    </label>
                                                    {fieldHelperText ? (
                                                      <div className="profile-payload-field-helper">
                                                        {fieldHelperText}
                                                      </div>
                                                    ) : null}
                                                    {validationMessages.length > 0 ? (
                                                      <div
                                                        id={messageId}
                                                        className="profile-payload-field-messages"
                                                      >
                                                        {validationMessages.map((message) => (
                                                          <div
                                                            key={`${message.severity}-${message.message}`}
                                                            className={`profile-payload-field-message is-${message.severity}`}
                                                          >
                                                            {message.message}
                                                          </div>
                                                        ))}
                                                      </div>
                                                    ) : null}
                                                  </div>
                                                </Col>
                                                {renderCompactTimingCell ? (
                                                  <Col
                                                    className={
                                                      isCompactIncomeGroup
                                                        ? 'profile-compact-income-field is-income-timing'
                                                        : 'profile-compact-expense-field is-expense-timing'
                                                    }
                                                  >
                                                    <div className="profile-payload-field-shell">
                                                      <label className="profile-payload-field">
                                                        <span>Frequency</span>
                                                        {amountTimingControl}
                                                      </label>
                                                    </div>
                                                  </Col>
                                                ) : null}
                                              </Fragment>
                                            );
                                          })}
                                          {isCompactProfileGroup && deleteControl ? (
                                            <Col
                                              flex="0 0 auto"
                                              className={
                                                isCompactIncomeGroup
                                                  ? 'profile-compact-income-action'
                                                  : isCompactBusinessGroup
                                                    ? 'profile-compact-business-action'
                                                    : 'profile-compact-expense-action'
                                              }
                                            >
                                              {deleteControl}
                                            </Col>
                                          ) : null}
                                        </Row>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </Card>
                          </div>
                        );
                      })}
                    </div>
                  ) : profileSearchTerm.trim() ? (
                    <div className="profile-payload-muted">
                      No profile fields match your search.
                    </div>
                  ) : (
                    <div className="profile-payload-muted">
                      No editable profile fields are available.
                    </div>
                  )}
                  <div className="profile-payload-actions">
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      onClick={handleProfilePatchSave}
                      loading={profilePatchSaving}
                      disabled={profilePatchSaveDisabled}
                    >
                      Apply update
                    </Button>
                  </div>
                  {profilePatchError ? (
                    <div className="profile-payload-error" role="alert">
                      {profilePatchError}
                    </div>
                  ) : null}
                  {profilePatchStatus ? (
                    <div className="profile-payload-status">{profilePatchStatus}</div>
                  ) : null}
                </div>
              </div>
            </>
          ) : profilePayloadLoading ? null : (
            <div className="profile-payload-muted">Investment profile is not available.</div>
          )}
        </section>
      ) : null}
    </div>
  );
};
export default WealthProfile;
